import React, { useCallback, useEffect, useMemo, useState } from "react";
import { clpInputFormatter, clpInputParser } from "../../utils/clpInput";
import {
  Tabs,
  Table,
  Button,
  Space,
  Input,
  Tag,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  Select,
  Popover,
  Popconfirm,
  Tooltip,
  Typography,
  Divider,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
} from "antd";
// IMPORTANTE: en antd v5 + React 19 los métodos estáticos `Modal.confirm` y
// `message.x` importados directo de "antd" se quedan mudos sin warning
// (crean roots React huérfanos). El proyecto tiene un bridge en
// utils/antdStatic.ts inicializado desde App.tsx con `App.useApp()`.
// Síntoma reportado en /sueldos (round 2026-05-01): el botón "Eliminar"
// no abría el modal de confirmación y los toasts no aparecían (lo que
// además enmascaraba errores de validación al crear sueldos esporádicos
// o regulares — el usuario veía "no funciona" cuando en realidad faltaba
// algún campo y el toast de error nunca se renderizaba).
// Se conserva el import del componente `<Modal>` (JSX) — solo los
// métodos estáticos están rotos.
import { getModal, getMsg } from "../../utils/antdStatic";
import { userSelectOption } from "../../utils/userDisplay";
import { formatPropertyName } from "../../utils/propertyDisplay";
import {
  ESTADO_VIGENCIA_TAG,
  estadoVigencia,
  rangoVigenciaLegible,
} from "../../utils/vigenciaRegular";
import type { ColumnsType } from "antd/es/table";
import {
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  SearchOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Filtros } from "./filtros";
import { apiFetch } from "../../services/apiClient";
import { FiltroFecha } from "./filtroGlobalFecha";
import { exportToXlsxMultiSheet } from "./exportXls";
import { compressImageFile } from "../../utils/imageCompression";
// Editor de imagen quitado (round 2026-04-30): el botón "Editar imagen" no se
// usaba en la práctica y se superponía con las miniaturas. Drag & drop + Ctrl+V
// cubren los casos reales (subir captura, foto del proveedor, etc.).
import {
  DragDropUpload,
  useClipboardPaste,
} from "../../Components/DragDropUpload";
import {
  listExpenseRecipients,
  type ExpenseRecipient,
} from "../../services/expenseRecipients.api";
import { ExpenseRecipientsManager } from "./ExpenseRecipientsManager";
import { SectionErrorBoundary } from "./SectionErrorBoundary";
import {
  AdjuntosCelda,
  TileAdjunto,
  VisorVideoModal,
  normalizarAdjuntos,
  type Adjunto,
} from "./adjuntos";
import { ahoraChile } from "../../utils/fechaChile";
import { paginacionCliente } from "../../utils/tablaPaginacion";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB — límite cliente para imágenes/videos

// Mapeo de categoría → etiqueta visible. El value en DB es ascii sin
// tilde (clave estable, no romper datos existentes); el label se
// muestra con ortografía correcta en español neutro. Antes se generaba
// con `c.charAt(0).toUpperCase() + c.slice(1)` y producía "Reparacion",
// "Computacion", "Fotografia", "Lavanderia", "Supervision" — todas sin
// tilde.
const CATEGORIA_LABELS: Record<string, string> = {
  reparacion: "Reparación",
  cuenta: "Cuenta",
  aseo: "Aseo",
  compra: "Compra",
  movimiento: "Movimiento",
  transporte: "Transporte",
  emergencia: "Emergencia",
  publicidad: "Publicidad",
  arriendo: "Arriendo",
  sueldo: "Sueldo",
  computacion: "Computación",
  fotografia: "Fotografía",
  lavanderia: "Lavandería",
  supervision: "Supervisión",
  impuestos: "Impuestos",
  reembolso: "Reembolso",
  otros: "Otros",
};

export type Periodicidad = "regular" | "esporadico";
export type Seccion = "Sueldos" | "Gastos";

export interface OpcionBasica {
  id: number;
  rol?: Rol;
  nombre: string;
}
type PagoEntidad = {
  tipo: 0 | 1 | 2 | 3 | 4 | 5;
  nombre: string;
  usuario_id?: number | null;
  recipient_id?: number | null;
};

export interface RegistroFinanza {
  id: string;
  tipo: Periodicidad;
  monto: number;
  fecha?: string;
  fechaPeriodica?: string;

  /**
   * Vigencia de un flujo REGULAR (round 2026-07-28 para sueldos; el mecanismo
   * es el del round 2026-07-26 en /flujos). La fila es UNA sola y vale por N
   * meses: la vigencia es lo que decide EN CUÁLES.
   *
   * `vigenciaInicio` null = "sin vigencia registrada" — sueldos creados antes
   * de que existiera el rango. Cuentan en todos los meses hasta que un humano
   * les ponga fechas. `vigenciaFin` null = indefinido (sigue vigente).
   */
  vigenciaInicio?: string | null;
  vigenciaFin?: string | null;

  pagadoPor: PagoEntidad | null;
  pagadoA: PagoEntidad | null;

  // opcional si te llega desde backend
  pagadoPorLabel?: string | null;
  pagadoALabel?: string | null;

  rolDeAlojaFacil?: string;
  // Round 2026-05-13 — para flujos tipo "intermediario" creados en /flujos
  // el backend materializa 2 rows enlazadas via `paired_id`. En la vista
  // readOnly de /finanzas-empresa lo usamos para NO doble-contar el
  // dinero (los lados 'paga' y 'recibe' del par se suman a Egresos y a
  // Ingresos respectivamente, pero como dinero AF neto no es real — es
  // dinero que solo "pasó por" AF). Convención: pairedId != null ⇒
  // intermediario; representante único = lado 'recibe' del par.
  pairedId?: number | null;
  // Round 2026-06-21 — los devuelve `expensesToFrontend`. Sirven para que el
  // KPI de la pestaña Gastos EXCLUYA las filas derivadas que el Resumen también
  // excluye (reembolso de inventario `paga`+inventoryTxId; espejo de "otros
  // cargos" del arriendo `reservaId`), y así ambos cuadren. Ver calcStatsFlujos.
  inventoryTxId?: number | null;
  reservaId?: number | null;
  lavanderiaId?: number | null;
  descripcion?: string;
  propiedadId?: number | null;
  imagenes?: string[];
  nombre?: string;
  categoria?: string | null;

  // (UI-only) puedes dejarlos para el modal
  pagadoARol: string;
  pagadoPorRol: string;
  pagadoPorUsuarioId?: number | null;
  pagadoAUsuarioId?: number | null;
  // Receptor externo (tipo 5) — solo UI; backend acepta como
  // `pagadoARecipientId` / `pagadoPorRecipientId` en payload separado.
  pagadoARecipientId?: number | null;
  pagadoPorRecipientId?: number | null;
}

// Round 2026-04-30 — el rol "otro" se renombró a "trabajador" para
// reflejar el verdadero significado: empleados de Aloja Fácil que no son
// aseadores ni propietarios (mantención, supervisor, contador externo).
// El backend normaliza ambos al mismo rol persistido. El tipo 4 en la
// PagoEntidad sigue siendo el mismo — sólo cambia el label.
const ROLES = [
  { value: "administrador", label: "Aloja Fácil" },
  // E3 (round 2026-06-20, D-A): tipo=1 = Cliente-gestor. El `value`
  // "propietario" se conserva (contrato del backend que mapea a tipo=1);
  // solo cambia el label visible. NO aplica al selector property-centric de
  // /flujos (caso aparte que lista dueños legales).
  { value: "propietario", label: "Cliente" },
  { value: "arrendatario", label: "Arrendatario" },
  { value: "aseador", label: "Aseador" },
  { value: "trabajador", label: "Trabajador" },
  { value: "receptor_externo", label: "Receptor externo" },
];

type Rol = (typeof ROLES)[number]["value"];

// Round 2026-05-12 — Restricción de roles de "Pagado a" según categoría.
// Un "reembolso" solo tiene sentido cuando AF le devuelve plata a un
// aseador o a un trabajador que adelantó dinero de su bolsillo por un
// gasto operativo. No se reembolsa a Aloja Fácil (auto-pago), ni a un
// propietario o arrendatario (esos flujos son liquidación/cobro, no
// reembolso), ni a un receptor externo (un proveedor no adelanta plata
// para que AF se la devuelva).
//
// Si en el futuro hay que restringir otra categoría, agregar otra entrada
// acá; las categorías que NO aparecen permiten cualquier rol.
const PAGADO_A_ROLES_POR_CATEGORIA: Record<string, Rol[]> = {
  reembolso: ["aseador", "trabajador"],
};

// Devuelve las opciones del Select de "Pagado a (rol)" filtradas por la
// categoría del gasto. Si la categoría no tiene restricción declarada,
// retorna el array completo de ROLES.
const getRolesPagadoA = (categoria?: string | null) => {
  if (!categoria) return ROLES;
  const allowed = PAGADO_A_ROLES_POR_CATEGORIA[categoria];
  if (!allowed) return ROLES;
  return ROLES.filter((r) => allowed.includes(r.value));
};

const PAGO_LABELS: Record<number, string> = {
  0: "Aloja Fácil",
  1: "Cliente", // E3 / D-A (2026-06-20): tipo=1 = Cliente-gestor, no dueño legal
  2: "Arrendatario",
  3: "Aseador",
  4: "Trabajador",
  5: "Receptor externo",
};

const RECEPTOR_INFO_TEXT = `¿Qué es un receptor externo? Cualquier OTRA EMPRESA que recibe dinero de Aloja Fácil y no es usuaria del sistema: servicios básicos (internet, agua, gas, electricidad), impuestos y trámites, tiendas y proveedores de insumos, o servicios profesionales (por ejemplo, un contador).

Una lavandería NUNCA es un receptor externo — ni una lavandería AF interna ni la Lavandería General Externa. Todo pago de lavandería se registra en /lavanderia, sobre la tabla de lavados: si lo cargas acá como gasto queda invisible para la reconciliación de /lavanderia y se cuenta dos veces en la utilidad. Por eso el sistema rechaza crear un receptor externo con el nombre de una lavandería del catálogo.

Tampoco son receptores externos los aseadores ni los trabajadores: cada uno tiene su propio rol en "Pagado a" y su propia ficha de finanzas.

Por defecto seleccionamos el receptor externo "Otro" — útil cuando no necesitas identificar al destinatario uno por uno. Si quieres desgloses más finos en los reportes, crea receptores externos específicos (ej. "Movistar", "Aguas Andinas") desde el botón "Gestionar" del selector.

Los receptores externos no son usuarios: no tienen cuenta, no pueden iniciar sesión y no tienen finanzas propias. Son solo una clasificación del flujo de dinero.`;

const pagoToLabel = (p?: PagoEntidad | null) => {
  if (!p) return "—";
  if (p.nombre && p.nombre !== PAGO_LABELS[p.tipo]) return p.nombre;
  return PAGO_LABELS[p.tipo] ?? p.nombre ?? "—";
};

const buildPagoFromRol = (
  rol: Rol,
  usuarioId: number | null | undefined,
  usuariosAll: OpcionBasica[],
  recipientId?: number | null,
  recipientsAll?: ExpenseRecipient[],
): PagoEntidad => {
  if (rol === "administrador")
    return { tipo: 0 as const, nombre: "Aloja Fácil", usuario_id: null };
  if (rol === "arrendatario")
    return { tipo: 2 as const, nombre: "Arrendatario", usuario_id: null };

  if (rol === "receptor_externo") {
    const r = recipientsAll?.find((x) => x.id === recipientId);
    return {
      tipo: 5 as const,
      nombre: r?.nombre || "Receptor externo",
      usuario_id: null,
      recipient_id: recipientId ?? null,
    };
  }

  // propietario, aseador, trabajador → resolve user name
  const u = usuariosAll.find((x) => x.id === usuarioId);
  if (rol === "propietario")
    // E3 / D-A: fallback "Cliente" (tipo=1 = Cliente-gestor).
    return { tipo: 1 as const, nombre: u?.nombre || "Cliente", usuario_id: usuarioId ?? null };
  if (rol === "aseador")
    return { tipo: 3 as const, nombre: u?.nombre || "Aseador", usuario_id: usuarioId ?? null };

  // rol === "trabajador" (antes "otro" — round 2026-04-30; tipo 4 se mantiene
  // por compatibilidad con `expenses.pagado_a/por.tipo` ya persistidos).
  return { tipo: 4 as const, nombre: u?.nombre || "Trabajador", usuario_id: usuarioId ?? null };
};

// ─── ErrorBoundary ──────────────────────────────────────────────────────────
// El boundary se extrajo a `SectionErrorBoundary.tsx` para que también lo
// puedan usar las pestañas que NO pasan por `FinanzasSection` (Arriendos,
// Aseos, Lavandería, Productos, Resumen) desde `finanzasEmpresa.tsx`. Acá
// solo lo importamos y lo aplicamos a Sueldos/Gastos como antes.

interface FinanzasSectionProps {
  titulo: Seccion;
  data: RegistroFinanza[];
  loading?: boolean;
  onCreate: (
    r: Omit<RegistroFinanza, "id">,
  ) => Promise<RegistroFinanza | void> | RegistroFinanza | void;
  onUpdate: (id: string, r: Partial<RegistroFinanza>) => Promise<void> | void;
  onDelete: (id: string) => Promise<void> | void;
  usuarios: OpcionBasica[];
  propiedades: OpcionBasica[];
  filtroFecha?: FiltroFecha;
  categoriaInicial?: string;
  /**
   * Prefijo con el que se recuerdan los filtros de ESTA sección por usuario
   * (round 2026-09-24). Lo pasa la página porque el mismo componente se monta
   * en `/sueldos` y en las pestañas Sueldos/Flujos de `/finanzas-empresa`, y
   * cada instancia tiene su propio buscador y su propio filtro avanzado. Si
   * no se pasa, se cae a una clave derivada del título para que las dos
   * instancias de `/finanzas-empresa` tampoco se pisen entre sí.
   */
  clavePersistencia?: string;
  /**
   * Contador de refresco manual (botón "Actualizar" del header). Al cambiar
   * re-consulta el backend con los filtros vigentes, sin tocar el mes.
   */
  refreshKey?: number;
  /**
   * Modo solo-lectura. Si `true`, oculta el botón "Nuevo X" y la
   * columna "Acciones" (Editar/Eliminar). Se usa cuando esta sección
   * se renderiza dentro de `/finanzas-empresa` (que pasa a ser puro
   * reporte). En las páginas dedicadas `/gastos` y `/sueldos`,
   * `readOnly` queda en `false` (default) y se ve el CRUD completo.
   */
  readOnly?: boolean;
}

export function FinanzasSection(props: FinanzasSectionProps) {
  return (
    <SectionErrorBoundary titulo={props.titulo}>
      <FinanzasSectionInner {...props} />
    </SectionErrorBoundary>
  );
}

function FinanzasSectionInner({
  titulo,
  onCreate,
  onUpdate,
  onDelete,
  usuarios,
  propiedades,
  filtroFecha,
  categoriaInicial,
  clavePersistencia,
  refreshKey,
  readOnly = false,
}: FinanzasSectionProps) {
  // Raíz de las claves con las que esta instancia recuerda sus filtros.
  const clave = clavePersistencia ?? `finanzasEmpresa.${titulo.toLowerCase()}`;
  // Búsqueda libre del lado cliente sobre la página actual: matchea contra
  // nombre, pagado a/por, descripción, propiedad, monto y fecha. Es
  // complementaria al filtro avanzado (que pega al backend con filtros
  // estructurados). Round 2026-04-30 — el usuario pidió "un buen buscador"
  // en /gastos y /sueldos para tipear y ver resultados al instante sin
  // abrir el panel de filtros.
  const [query, setQuery] = useFiltroPersistido(`${clave}.busqueda`, "", {
    validar: (v) => typeof v === "string",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<RegistroFinanza | null>(null);
  // Adjuntos del flujo en edición. Son los entries completos (url, thumbUrl, previewUrl, kind,
  // key) y NO solo URLs: el tile pinta la miniatura, la vista ampliada la foto completa y el
  // video abre con su versión liviana. Se gestionan por los endpoints dedicados
  // (`/expenses/:id/images`), no por el payload del formulario.
  const [expenseImages, setExpenseImages] = useState<Adjunto[]>([]);
  const [videoAbierto, setVideoAbierto] = useState<Adjunto | null>(null);
  const [savingImage, setSavingImage] = useState(false);
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [pendingPreviews, setPendingPreviews] = useState<string[]>([]);
  // Estado del submit del modal — habilita el spinner del botón Crear/Guardar
  // y bloquea clicks múltiples (antes el usuario podía clickear varias veces
  // mientras procesaba sin saber que ya había disparado la creación).
  const [submitting, setSubmitting] = useState(false);

  // "Terminar" un flujo regular (round 2026-07-28). Es la acción que faltaba:
  // el dueño necesitaba DETENER un sueldo sin borrarlo, y hasta ahora la única
  // forma de que dejara de aparecer era eliminar la fila — que se lleva
  // también todos los meses ya pagados. Ponerle término lo apaga de ahí en
  // adelante y deja el historial donde está.
  const [terminando, setTerminando] = useState<RegistroFinanza | null>(null);
  const [mesTermino, setMesTermino] = useState<any>(null);
  const [terminandoLoading, setTerminandoLoading] = useState(false);

  // Selección masiva para borrado en lote. Solo activa cuando NO estamos
  // en modo readOnly (en /finanzas-empresa la sección es puro reporte y
  // no se borra nada desde ahí). Mismo patrón que el de /inventario:
  // checkboxes nativos de AntD + barra contextual + Popconfirm.
  // `bulkDeleting` evita doble-click durante el await del Promise.all.
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const handleBulkDelete = async () => {
    if (!selectedRowKeys.length) return;
    setBulkDeleting(true);
    const ids = selectedRowKeys.map((k) => String(k));
    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      try {
        // onDelete es el handler ya inyectado por el padre — borra UNO por UNO
        // contra el mismo endpoint que usa "Eliminar" individual. No hay
        // bulk endpoint en backend, así que iteramos.
        await onDelete(id);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setSelectedRowKeys([]);
    // Refetch obligatorio: el padre (`useFinanzasEmpresaLogic.deleteGasto`)
    // sólo actualiza su `rows` interno, pero el `data` que pinta esta tabla
    // vive en el estado local de la sección y se popula vía
    // `/finanzas/empresa/gastos/search`. Sin este refetch los rows ya
    // borrados seguían visibles, el usuario los re-seleccionaba y el
    // segundo click devolvía 404 "No encontrado" desde el backend.
    // Round 2026-05-01.
    await fetchFinanzas(latestFiltersRef.current, page);
    setBulkDeleting(false);
    if (ok > 0 && fail === 0) {
      getMsg()?.success(`${ok} registro${ok === 1 ? "" : "s"} eliminado${ok === 1 ? "" : "s"}`);
    } else if (ok > 0 && fail > 0) {
      getMsg()?.warning(`${ok} eliminado${ok === 1 ? "" : "s"}, ${fail} con error`);
    } else {
      getMsg()?.error("No se pudo eliminar ningún registro");
    }
  };

  // Sube/encola un File (imagen o video). Si es imagen, primero la
  // comprime con `compressImageFile` aplicando los parámetros de la config
  // admin. Videos van tal cual. Límite cliente (25 MB) sobre el original.
  // Si hay un expense en edición, sube al endpoint; si es creación nueva,
  // lo encola en pendingImages para subir después de crear el expense.
  const addExpenseFile = async (file: File) => {
    if (file.size > MAX_UPLOAD_BYTES) {
      getMsg()?.error(
        `Archivo demasiado grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo permitido: 25 MB.`,
      );
      return;
    }
    try {
      setSavingImage(true);
      const toUpload = await compressImageFile(file);
      if (editing) {
        const formData = new FormData();
        formData.append("file", toUpload, toUpload.name);
        const json = await apiFetch<{ data: any; item?: any }>(
          `/finanzas/empresa/expenses/${editing.id}/images`,
          { method: "POST", body: formData },
        );
        if (!json) throw new Error("Error subiendo imagen");
        // `item` es el entry completo; `data` (solo la URL) queda de respaldo.
        setExpenseImages((prev) => [
          ...prev,
          ...normalizarAdjuntos([json.item ?? json.data]),
        ]);
      } else {
        setPendingImages((prev) => [...prev, toUpload]);
        setPendingPreviews((prev) => [...prev, URL.createObjectURL(toUpload)]);
      }
      getMsg()?.success(file.type.startsWith("video/") ? "Video agregado" : "Imagen agregada");
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error guardando archivo");
    } finally {
      setSavingImage(false);
    }
  };

  // Ctrl+V para pegar imagen/video desde el portapapeles. Activo mientras el
  // modal está abierto. Convención del proyecto: TODA zona de upload soporta
  // drag&drop + paste (ver docs/storage-y-visor.md §6).
  useClipboardPaste({
    active: modalOpen,
    onFile: (file) => {
      void addExpenseFile(file);
    },
    accept: "image/*,video/*",
  });
  const [form] = Form.useForm<RegistroFinanza>();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  // El getter no se usa (se actualiza para el paginador, pero no hay "Total: N" UI).
  const [, setTotal] = useState(0);

  // El filtro avanzado se recuerda. `<Filtros value={filters}>` ya recibe el
  // valor, así que el panel abre mostrando lo que está aplicado (si no, la
  // tabla saldría recortada por un filtro invisible).
  // `?categoria=` es un enlace directo desde las cards de /finanzas-empresa y
  // manda sobre lo guardado.
  const [filters, setFilters] = useFiltroPersistido<Record<string, any>>(
    `${clave}.filtros`,
    categoriaInicial ? { categoria: [categoriaInicial] } : {},
    {
      validar: (v) => !!v && typeof v === "object" && !Array.isArray(v),
      restaurar: !categoriaInicial,
    },
  );
  const [usuariosLocal, setUsuariosLocal] = useState<OpcionBasica[]>(
    usuarios as any,
  );
  useEffect(() => setUsuariosLocal(usuarios as any), [usuarios]);

  // ── Receptores externos (tipo 5) ────────────────────────────────────────
  const [recipients, setRecipients] = useState<ExpenseRecipient[]>([]);
  const [recipientsManagerOpen, setRecipientsManagerOpen] = useState(false);
  const [recipientsManagerInitialMode, setRecipientsManagerInitialMode] =
    useState<"list" | "create">("list");

  // Id del receptor "Otro" (protegido por backend) — usado como default
  // del campo `pagadoARecipientId` cuando se crea un gasto nuevo.
  // Defensivo: si algún recipient llega con `nombre` null (el modelo no
  // valida NOT NULL en backend → posible row corrupto), `null.trim()`
  // revienta el render → SectionErrorBoundary captura → "No se pudo
  // cargar la pestaña Gastos". Con `?? ""` el bug se vuelve no-fatal.
  const otroRecipientId = useMemo(
    () =>
      recipients.find((r) => (r.nombre ?? "").trim().toLowerCase() === "otro")
        ?.id ?? null,
    [recipients],
  );

  // Mismo patrón defensivo: cualquier comparación string sobre `nombre`
  // debe tolerar null/undefined para no tirar la pestaña Gastos completa.
  const activeRecipients = useMemo(
    () =>
      recipients
        .filter((r) => r.activo)
        .sort((a, b) => {
          const aOtro =
            (a.nombre ?? "").trim().toLowerCase() === "otro" ? 0 : 1;
          const bOtro =
            (b.nombre ?? "").trim().toLowerCase() === "otro" ? 0 : 1;
          if (aOtro !== bOtro) return aOtro - bOtro;
          return (a.nombre ?? "").localeCompare(b.nombre ?? "");
        }),
    [recipients],
  );

  const recipientOptions = useMemo(
    () =>
      activeRecipients.map((r) => ({
        value: r.id,
        label: r.nombre,
      })),
    [activeRecipients],
  );

  const refreshRecipients = useCallback(async () => {
    const data = await listExpenseRecipients(false);
    if (data) setRecipients(data);
    return data;
  }, []);

  useEffect(() => {
    refreshRecipients();
  }, [refreshRecipients]);

  const openRecipientsManager = (mode: "list" | "create" = "list") => {
    setRecipientsManagerInitialMode(mode);
    setRecipientsManagerOpen(true);
  };

  const handleRecipientsManagerChange = (
    updated: ExpenseRecipient[],
    lastCreated?: ExpenseRecipient,
  ) => {
    setRecipients(updated);
    if (lastCreated) {
      // Si el usuario abrió el manager desde un + Crear receptor inline en
      // "Pagado a", autoseleccionamos el recién creado para no perder contexto.
      const currentRol = form.getFieldValue("pagadoARol");
      if (currentRol === "receptor_externo") {
        form.setFieldValue("pagadoARecipientId", lastCreated.id);
      }
    }
  };

  const usuarioMap = useMemo(
    () => new Map(usuarios.map((u) => [u.id, u.nombre])),
    [usuarios],
  );

  const propiedadMap = useMemo(
    () => new Map(propiedades.map((p) => [p.id, p.nombre])),
    [propiedades],
  );

  // Mapeo `rol DB → bucket UI`. Round 2026-04-30 decies — antes el filtro
  // requería que `u.rol` matcheara exacto un value de ROLES (UI), lo que
  // dejaba a los clientes (rol DB="cliente") fuera del bucket
  // "propietario" del UI y al usuario seleccionando "Pagado a (rol) =
  // Propietario" no le aparecía nadie. Ahora hacemos el mapeo explícito:
  //  - cliente DB → bucket "propietario" (cliente == propietario)
  //  - aseador DB → bucket "aseador"
  //  - trabajador DB → bucket "trabajador"
  //  - administrador / superadministrador → ningún bucket (no se eligen
  //    como usuario individual; se representan como tipo 0 sin usuario_id)
  const usuariosPorRol = useMemo(() => {
    const m = new Map<Rol, OpcionBasica[]>();
    ROLES.forEach((r) => m.set(r.value, []));

    usuariosLocal.forEach((u: any) => {
      const r = String(u.rol ?? "").trim();
      if (r === "cliente") m.get("propietario")?.push(u);
      else if (r === "aseador") m.get("aseador")?.push(u);
      else if (r === "trabajador") m.get("trabajador")?.push(u);
    });

    return m;
  }, [usuariosLocal]);

  // Inline-create de un usuario "Trabajador" (rol sin login) desde el
  // dropdown de "Pagado a/por". Round 2026-04-30 — antes mandaba
  // `rol: "otro"` y el backend lo normalizaba a "cliente", lo que
  // rompía el filtro `usuariosPorRol` ("No hay datos" en el screenshot
  // del usuario). Ahora envía directamente "trabajador" — el backend
  // acepta ambos como alias (ver crearUsuarioSimple).
  const crearTrabajador = async (nombre: string) => {
    const json = await apiFetch<OpcionBasica>("/users/simple", {
      method: "POST",
      body: JSON.stringify({ rol: "trabajador", nombre }),
    });
    if (!json) throw new Error("Error creando usuario");
    return json;
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return data.filter((r) => {
      // "Pagado a" / "Pagado por" son objetos JSON {tipo, nombre, usuario_id}
      // — se resuelven con el MISMO helper que usa la tabla (pagoToLabel).
      // Antes el haystack leía campos fantasma (pagadoATipo/pagadoAOtroNombre,
      // que el backend jamás emite) y metía el objeto pagadoPor crudo, que se
      // serializaba como "[object Object]" (auditoría 2026-07-13).
      const pagadoANombre = r.pagadoA
        ? pagoToLabel(r.pagadoA)
        : r.pagadoAUsuarioId
          ? (usuarioMap.get(r.pagadoAUsuarioId) ?? "")
          : "";
      const pagadoPorNombre = r.pagadoPor ? pagoToLabel(r.pagadoPor) : "";
      const propiedadNombre = r.propiedadId
        ? propiedadMap.get(r.propiedadId)
        : "";
      const hay = [
        r.nombre,
        pagadoPorNombre,
        pagadoANombre,
        r.descripcion,
        r.rolDeAlojaFacil,
        r.fecha,
        // La tabla muestra la fecha DD-MM-YYYY: que buscarla como se ve
        // también matchee (la cruda YYYY-MM-DD se mantiene arriba).
        r.fecha ? dayjs(r.fecha).format("DD-MM-YYYY") : "",
        r.fechaPeriodica,
        propiedadNombre,
        String(r.monto ?? ""),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [data, query, usuarioMap, propiedadMap]);

  const columnsBase: ColumnsType<RegistroFinanza> = [
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      width: 180,
      render: (v) => <b>{v}</b>,
      sorter: (a, b) => (a.nombre ?? "").localeCompare(b.nombre ?? ""),
    },
    {
      title: "Monto",
      dataIndex: "monto",
      key: "monto",
      width: 120,
      sorter: (a, b) => a.monto - b.monto,
      render: (v) =>
        new Intl.NumberFormat("es-CL", {
          style: "currency",
          currency: "CLP",
        }).format(v ?? 0),
    },
    {
      title: "Fecha",
      key: "fecha",
      width: 210,
      sorter: (a: any, b: any) => dayjs(a.fecha).unix() - dayjs(b.fecha).unix(),
      render: (_, r) => {
        if (r.tipo !== "regular") {
          return <span>{r.fecha ? dayjs(r.fecha).format("DD-MM-YYYY") : "—"}</span>;
        }
        // Un regular necesita las dos cosas para leerse sin ambigüedad: QUÉ DÍA
        // del mes se paga y EN QUÉ MESES rige. Mostrar solo el día hacía creer
        // que el sueldo vale para siempre.
        const est = estadoVigencia(r);
        const rango = rangoVigenciaLegible(r);
        return (
          <Space direction="vertical" size={2}>
            <Space size={4}>
              <Tag color="geekblue" style={{ margin: 0 }}>
                día {r.fechaPeriodica}
              </Tag>
              <Tag color={ESTADO_VIGENCIA_TAG[est].color} style={{ margin: 0 }}>
                {ESTADO_VIGENCIA_TAG[est].texto}
              </Tag>
            </Space>
            <span style={{ fontSize: 12, opacity: 0.75 }}>{rango}</span>
          </Space>
        );
      },
    },
    {
      title: "Pagado a",
      key: "pagadoACompuesto",
      width: 200,
      ellipsis: true,
      render: (_, r: any) => pagoToLabel(r.pagadoA),
    },
    // En Sueldos el "Pagado por" siempre es "Aloja Fácil" (invariante
    // §3.3.0 del MD): mostrar la columna sería ruido visual sin información
    // útil. La ocultamos en Sueldos y la mantenemos en Flujos donde sí
    // varía (admin / propietario / arrendatario / etc.).
    ...(titulo === "Sueldos"
      ? []
      : [
          {
            title: "Pagado por",
            key: "pagadoPorCompuesto",
            width: 200,
            ellipsis: true,
            render: (_: any, r: any) => pagoToLabel(r.pagadoPor),
          },
        ]),
    {
      title: "Descripción",
      dataIndex: "descripcion",
      key: "descripcion",
      // Antes: sin width → la descripción se comía la pantalla cuando había
      // textos largos y la tabla no cabía completa. Ahora width fijo + ellipsis,
      // y el detalle completo se ve al pasar el mouse en un Popover formateado
      // (mejor que el tooltip estándar de AntD que renderea un cuadradito de
      // texto plano e ilegible). Round 2026-04-30.
      width: 280,
      ellipsis: true,
      sorter: (a, b) =>
        (a.descripcion ?? "").localeCompare(b.descripcion ?? ""),
      render: (v?: string | null) => {
        const text = (v ?? "").trim();
        if (!text) return <span style={{ color: "#999" }}>—</span>;
        return (
          <Popover
            placement="topLeft"
            trigger={["hover", "click"]}
            mouseEnterDelay={0.3}
            content={
              <div style={{ maxWidth: 480, maxHeight: 320, overflow: "auto" }}>
                <Typography.Paragraph
                  style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}
                >
                  {text}
                </Typography.Paragraph>
              </div>
            }
          >
            <span
              style={{
                display: "inline-block",
                maxWidth: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                cursor: "help",
              }}
            >
              {text}
            </span>
          </Popover>
        );
      },
    },
    ...(titulo === "Gastos"
      ? [
          {
            title: "Categoría",
            dataIndex: "categoria",
            key: "categoria",
            width: 130,
            sorter: (a: RegistroFinanza, b: RegistroFinanza) =>
              (a.categoria ?? "").localeCompare(b.categoria ?? ""),
            render: (v?: string | null) => {
              if (!v) return "—";
              const label =
                CATEGORIA_LABELS[v] ?? v.charAt(0).toUpperCase() + v.slice(1);
              return <Tag color="orange">{label}</Tag>;
            },
          },
        ]
      : []),
    // Columna Propiedad — solo en Gastos (round 2026-04-30). En Sueldos
    // se omite porque un sueldo no está asociado a una propiedad.
    ...(titulo !== "Sueldos"
      ? [
          {
            title: "Propiedad",
            dataIndex: "propiedadId",
            key: "propiedadId",
            width: 180,
            render: (id?: number | null) =>
              id ? formatPropertyName(propiedadMap.get(id)) || "—" : "—",
            sorter: (a: RegistroFinanza, b: RegistroFinanza) => {
              const nombreA = a.propiedadId
                ? propiedadMap.get(a.propiedadId) || ""
                : "";
              const nombreB = b.propiedadId
                ? propiedadMap.get(b.propiedadId) || ""
                : "";
              return nombreA.localeCompare(nombreB);
            },
          },
        ]
      : []),
    {
      title: "Imágenes",
      dataIndex: "imagenes",
      key: "imagenes",
      width: 160,
      // La celda pinta la MINIATURA de cada adjunto (thumbUrl); la vista ampliada abre la foto
      // completa y un video se reproduce con su versión liviana. Ver ./adjuntos.tsx.
      render: (list?: unknown[]) => <AdjuntosCelda adjuntos={normalizarAdjuntos(list)} />,
    },
    // Columna "Acciones" se omite en modo solo-lectura (cuando esta
    // sección se monta dentro de `/finanzas-empresa` como reporte).
    ...(readOnly
      ? []
      : [
          {
            // Width 180 (antes 140): Editar + Eliminar + gap del Space
            // pasaban los 140px y el botón "Eliminar" quedaba cortado en
            // el borde derecho (column es fixed:right, así que el corte
            // se nota inmediato).
            title: "Acciones",
            key: "acciones",
            fixed: "right" as const,
            width: 280,
            render: (_: any, r: RegistroFinanza) => (
              <Space>
                <Button onClick={() => onEdit(r)}>Editar</Button>
                {/* Solo para regulares: un esporádico ya tiene su fecha y no
                    hay nada que "terminar". */}
                {r.tipo === "regular" &&
                  (r.vigenciaInicio ? (
                    <Tooltip title="Deja de pagarse a partir del mes que elijas. Los meses anteriores no se tocan.">
                      <Button onClick={() => abrirTerminar(r)}>Terminar</Button>
                    </Tooltip>
                  ) : (
                    // Sin inicio no se puede poner término: el backend exige el
                    // rango completo. Se manda a Editar en vez de fallar con un
                    // 400 que no explicaría nada.
                    <Tooltip title="Primero define desde qué mes rige, con el botón Editar. Sin fecha de inicio no se le puede poner término.">
                      <Button disabled>Terminar</Button>
                    </Tooltip>
                  ))}
                <Button danger onClick={() => onConfirmDelete(r.id, r)}>
                  Eliminar
                </Button>
              </Space>
            ),
          },
        ]),
  ];

  const onEdit = (r?: RegistroFinanza) => {
    const record = r ?? null;
    setEditing(record);
    setModalOpen(true);
    setExpenseImages(normalizarAdjuntos(record?.imagenes));

    if (record) {
      // Inferir rol desde el usuario seleccionado (para que el filtro por rol funcione)
      const pagadoAUser = usuarios.find(
        (u: any) => u.id === record.pagadoAUsuarioId,
      );
      const pagadoPorUser = usuarios.find(
        (u: any) => u.id === record.pagadoPorUsuarioId,
      );

      // Si el registro venía con un PagoEntidad de tipo 5 (receptor externo),
      // inferimos el rol y el recipient_id desde el JSON.
      const pagadoAIsRecipient = record.pagadoA?.tipo === 5;
      const pagadoPorIsRecipient = record.pagadoPor?.tipo === 5;

      form.setFieldsValue({
        ...record,
        fecha: record.fecha ? (dayjs(record.fecha) as any) : undefined,

        // Vigencia: `undefined` cuando la fila no la tiene (regular anterior
        // al round). Los campos quedan vacíos y la regla `required` obliga a
        // completarlos antes de guardar — es la forma de ir saldando los
        // sueldos viejos sin inventarles una fecha de inicio que nadie sabe.
        vigenciaInicio: record.vigenciaInicio
          ? (dayjs(record.vigenciaInicio) as any)
          : undefined,
        vigenciaFin: record.vigenciaFin
          ? (dayjs(record.vigenciaFin) as any)
          : undefined,

        // En /sueldos no guardamos propiedad — si un registro legacy venía
        // con propiedadId, se limpia al editar. Round 2026-04-30.
        ...(titulo === "Sueldos" ? { propiedadId: null } : {}),

        // UI fields (si los tienes en el Form)
        pagadoARol: pagadoAIsRecipient
          ? "receptor_externo"
          : (pagadoAUser?.rol ?? record.pagadoARol ?? "trabajador"),
        pagadoPorRol: pagadoPorIsRecipient
          ? "receptor_externo"
          : (pagadoPorUser?.rol ?? record.pagadoPorRol ?? "administrador"),
        pagadoARecipientId: pagadoAIsRecipient
          ? (record.pagadoA?.recipient_id ?? record.pagadoARecipientId ?? null)
          : (record.pagadoARecipientId ?? null),
        pagadoPorRecipientId: pagadoPorIsRecipient
          ? (record.pagadoPor?.recipient_id ?? record.pagadoPorRecipientId ?? null)
          : (record.pagadoPorRecipientId ?? null),
      } as any);
    } else {
      form.resetFields();

      // Round 2026-04-30 octies — defaults sensibles según pestaña.
      // - "Sueldos": el flujo más común es AF paga a un aseador.
      //   Pre-seleccionamos `pagadoARol="aseador"` y `categoria="sueldo"`
      //   para que el form arranque cerca del estado correcto y admin
      //   solo tenga que pickear el aseador específico. Antes el default
      //   era `receptor_externo` y se daban casos donde admin cambiaba
      //   a "aseador" pero olvidaba pickear el usuario, con lo cual el
      //   sueldo se persistía con `usuario_id=null` y nunca aparecía en
      //   /finanzas-aseador-admin. Ahora el backend además rechaza
      //   tipo=3 con usuario_id=null (defense-in-depth).
      // - "Gastos": el más común es pago a un receptor externo (caja
      //   chica, proveedor) → mantener `receptor_externo` como antes.
      const isSueldosTab = titulo === "Sueldos";
      form.setFieldsValue({
        // Default: esporádico con fecha = hoy. La opción "regular" (gasto
        // periódico que se repite cada mes en un día fijo) sigue disponible
        // en el selector de Tipo, pero la mayoría de los gastos son fechas
        // concretas, así que es el default más útil.
        tipo: "esporadico",
        fecha: ahoraChile(),
        // Si el admin cambia el tipo a "regular", el mes de inicio ya viene
        // puesto en el actual — que es el caso casi siempre. Como esporádico
        // no se envía (el payload solo manda vigencia cuando tipo=regular),
        // dejarlo precargado no ensucia nada.
        vigenciaInicio: ahoraChile(),
        vigenciaFin: undefined,
        rolDeAlojaFacil: "paga",
        ...(isSueldosTab
          ? {
              // Sueldo → Aloja Fácil paga a un aseador.
              categoria: "sueldo",
              pagadoARol: "aseador",
              pagadoAUsuarioId: null,
              pagadoARecipientId: null,
            }
          : {
              // Gasto → receptor externo "Otro" por default.
              pagadoARol: "receptor_externo",
              pagadoAUsuarioId: null,
              pagadoARecipientId: otroRecipientId,
            }),

        // pagado por sigue siendo Aloja Fácil por default.
        pagadoPorRol: "administrador",
        pagadoPorUsuarioId: null,
        pagadoPorRecipientId: null,
      } as any);
    }
  };

  /** Abre el diálogo de término con el mes actual (o el que ya tuviera). */
  const abrirTerminar = (r: RegistroFinanza) => {
    setTerminando(r);
    setMesTermino(r.vigenciaFin ? dayjs(r.vigenciaFin) : ahoraChile());
  };

  const confirmarTerminar = async () => {
    if (!terminando || !mesTermino) return;
    setTerminandoLoading(true);
    try {
      await onUpdate(terminando.id, {
        // El backend valida la vigencia COMPLETA cuando se toca la de un
        // regular, así que hay que reenviarle el inicio que ya tenía. Acá solo
        // se está fijando el término.
        vigenciaInicio: dayjs(terminando.vigenciaInicio).format("YYYY-MM-DD"),
        vigenciaFin: dayjs(mesTermino).format("YYYY-MM-DD"),
      });
      setTerminando(null);
      await fetchFinanzas(latestFiltersRef.current, page);
      getMsg()?.success(
        `Termina en ${dayjs(mesTermino).format("MM/YYYY")}. Los meses anteriores no se tocaron.`,
      );
    } catch (e) {
      console.error("Error al terminar el flujo regular:", e);
    } finally {
      setTerminandoLoading(false);
    }
  };

  const onConfirmDelete = (id: string, registro?: RegistroFinanza) => {
    // Round 2026-05-01 — bug reportado: el botón "Eliminar" en /sueldos no
    // hacía nada al hacer clic. Causa: `Modal.confirm` importado de "antd"
    // no funciona en antd v5 + React 19 (root React huérfano fuera del
    // ConfigProvider/App). Migrado al bridge `getModal()` que captura la
    // instancia real desde `App.useApp()` en App.tsx.
    const modal = getModal();
    const doDelete = async () => {
      await onDelete(id);
      await fetchFinanzas(latestFiltersRef.current, page);
      getMsg()?.success("Eliminado");
    };
    if (!modal) {
      // Fallback defensivo: si por alguna razón el bridge aún no está
      // inicializado al primer clic, ejecutamos directo. Es preferible
      // borrar sin confirmación a quedarse mudo (el usuario igualmente
      // hizo clic explícito en "Eliminar").
      void doDelete();
      return;
    }
    // Borrar un REGULAR no es "borrar el de este mes": la fila es UNA y vale
    // por todos los meses de su vigencia, así que se lleva también los ya
    // pagados. Es el reclamo textual del dueño («se me borra todo el pasado»).
    // Decirlo acá, con la alternativa al lado, evita el borrado que no se
    // puede deshacer.
    const esRegular = registro?.tipo === "regular";
    modal.confirm({
      title: esRegular ? "¿Eliminar este registro regular?" : "¿Eliminar registro?",
      content: esRegular
        ? "Se borra por completo, incluidos los meses ya transcurridos: desaparece de los reportes pasados como si nunca hubiera existido. Si lo que quieres es que deje de pagarse de ahora en adelante, cancela y usa «Terminar» — eso conserva el historial."
        : undefined,
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      width: esRegular ? 520 : undefined,
      onOk: doDelete,
    });
  };

  // ── Sync entre "Rol de Aloja Fácil" y "Pagado por/a" ─────────────────
  // Round 2026-04-30 — el usuario pidió que estos campos estén
  // "prefijados, no deja otra, están relacionados". La regla:
  //   • Rol AF = "paga"          ↔ pagadoPorRol = administrador (Aloja Fácil)
  //   • Rol AF = "recibe"        ↔ pagadoARol   = administrador
  //   • Rol AF = "intermediario" ↔ ningún lado es administrador
  //
  // Implementación: dos helpers idempotentes. Cuando el usuario cambia
  // uno, sincronizamos el otro. Se llaman desde el onChange de cada
  // Select. NO usar setFieldsValue dentro del render — usarlos solo en
  // handlers de evento.

  /**
   * Recalcula `rolDeAlojaFacil` a partir de `pagadoPorRol` y `pagadoARol`.
   * Llamado cuando el usuario cambia uno de los dos selects de pagado.
   */
  const syncRolAFFromPagados = () => {
    const por = form.getFieldValue("pagadoPorRol");
    const a = form.getFieldValue("pagadoARol");
    let next: "paga" | "recibe" | "intermediario" | undefined;
    if (por === "administrador" && a !== "administrador") next = "paga";
    else if (a === "administrador" && por !== "administrador") next = "recibe";
    else if (por && a && por !== "administrador" && a !== "administrador")
      next = "intermediario";
    if (next) form.setFieldValue("rolDeAlojaFacil", next);
  };

  /**
   * Aplica el efecto de cambiar `rolDeAlojaFacil` sobre `pagadoPorRol` y
   * `pagadoARol`. Si AF "paga", forzamos pagadoPor=administrador y
   * limpiamos el otro lado si quedó como administrador (ambos admin es
   * inválido). Idem espejado para "recibe". Para "intermediario"
   * limpiamos cualquier lado que estuviera en administrador.
   */
  const syncPagadosFromRolAF = (val: "paga" | "recibe" | "intermediario") => {
    if (val === "paga") {
      form.setFieldValue("pagadoPorRol", "administrador");
      form.setFieldValue("pagadoPorUsuarioId", null);
      form.setFieldValue("pagadoPorRecipientId", null);
      if (form.getFieldValue("pagadoARol") === "administrador") {
        // Ambos admin es inválido — dejar pagadoARol vacío para que el
        // usuario elija a quién paga AF. Required validation lo detendrá
        // si no completa.
        form.setFieldValue("pagadoARol", undefined);
        form.setFieldValue("pagadoAUsuarioId", null);
        form.setFieldValue("pagadoARecipientId", null);
      }
    } else if (val === "recibe") {
      form.setFieldValue("pagadoARol", "administrador");
      form.setFieldValue("pagadoAUsuarioId", null);
      form.setFieldValue("pagadoARecipientId", null);
      if (form.getFieldValue("pagadoPorRol") === "administrador") {
        form.setFieldValue("pagadoPorRol", undefined);
        form.setFieldValue("pagadoPorUsuarioId", null);
        form.setFieldValue("pagadoPorRecipientId", null);
      }
    } else {
      // intermediario — ningún lado puede ser administrador.
      if (form.getFieldValue("pagadoPorRol") === "administrador") {
        form.setFieldValue("pagadoPorRol", undefined);
        form.setFieldValue("pagadoPorUsuarioId", null);
        form.setFieldValue("pagadoPorRecipientId", null);
      }
      if (form.getFieldValue("pagadoARol") === "administrador") {
        form.setFieldValue("pagadoARol", undefined);
        form.setFieldValue("pagadoAUsuarioId", null);
        form.setFieldValue("pagadoARecipientId", null);
      }
    }
  };

  const submit = async () => {
    // Guard contra doble-click: si ya estamos procesando, ignorar. AntD Modal
    // también pasa `confirmLoading` al botón OK pero el handler `onOk` puede
    // dispararse igual si el usuario clickea muy rápido — duplicaba el create
    // y el usuario no se daba cuenta hasta ver dos rows iguales en la tabla.
    if (submitting) return;
    try {
      setSubmitting(true);
      const values = await form.validateFields();
      const pagadoPor = buildPagoFromRol(
        values.pagadoPorRol,
        values.pagadoPorUsuarioId,
        usuariosLocal,
        values.pagadoPorRecipientId,
        recipients,
      );

      const pagadoA = buildPagoFromRol(
        values.pagadoARol,
        values.pagadoAUsuarioId,
        usuariosLocal,
        values.pagadoARecipientId,
        recipients,
      );
      const payload: Omit<RegistroFinanza, "id"> = {
        ...values,
        // El DÍA que el usuario ve en el picker, no el instante. Antes iba
        // `.toDate().toISOString()`, y el backend recortaba ese ISO en UTC: un
        // flujo cargado a las 21:30 del 31 de agosto viajaba como
        // "2026-09-01T01:30:00.000Z" y quedaba guardado el 1 de SEPTIEMBRE, en
        // `gastos.fecha` (DATEONLY), de forma permanente. El corte era a las
        // 20:00 (21:00 en horario de verano) y no dependía de la zona del
        // dispositivo: pasaba en un equipo chileno bien configurado. Cargar
        // gastos de noche es lo habitual, así que el sesgo era sistemático.
        // Mismo patrón que `vigenciaInicio`/`vigenciaFin` acá abajo.
        fecha: values.fecha ? (values.fecha as any).format("YYYY-MM-DD") : undefined,
        // Vigencia SOLO para regulares: el backend rechaza que un esporádico
        // la traiga (tiene su propia `fecha`). Se manda 'YYYY-MM-DD' y el
        // backend normaliza al primer/último día del mes.
        vigenciaInicio:
          values.tipo === "regular" && values.vigenciaInicio
            ? dayjs(values.vigenciaInicio).format("YYYY-MM-DD")
            : null,
        vigenciaFin:
          values.tipo === "regular" && values.vigenciaFin
            ? dayjs(values.vigenciaFin).format("YYYY-MM-DD")
            : null,
        // En Sueldos forzamos propiedadId=null aunque AntD haya
        // arrastrado un valor desde un edit previo de un registro
        // legacy (round 2026-04-30).
        ...(titulo === "Sueldos" ? { propiedadId: null } : {}),
        pagadoPor,
        pagadoA,
        // Backend acepta `pagadoARecipientId`/`pagadoPorRecipientId` como
        // campos separados en el body, además del PagoEntidad anidado. Los
        // mandamos siempre — backend los ignora si tipo != 5.
        pagadoARecipientId:
          values.pagadoARol === "receptor_externo"
            ? values.pagadoARecipientId ?? null
            : null,
        pagadoPorRecipientId:
          values.pagadoPorRol === "receptor_externo"
            ? values.pagadoPorRecipientId ?? null
            : null,
        // OJO: NO se manda `imagenes`. Los adjuntos se suben y se borran por sus endpoints
        // (`/expenses/:id/images`); mandarlos acá hacía que el PUT reemplazara la lista
        // guardada (entries con key de storage) por un arreglo de URLs y los archivos
        // quedaban sin dueño. Sin el campo, el backend deja la lista como está.
      };

      if (editing) {
        await onUpdate(editing.id, payload);
        getMsg()?.success("Actualizado");
      } else {
        const created = await onCreate(payload);
        if (created?.id && pendingImages.length > 0) {
          for (const file of pendingImages) {
            const fd = new FormData();
            fd.append("file", file, file.name || `expense-${created.id}.jpg`);
            await apiFetch(
              `/finanzas/empresa/expenses/${created.id}/images`,
              { method: "POST", body: fd },
            );
          }
        }
        setPendingImages([]);
        setPendingPreviews((prev) => { prev.forEach((u) => URL.revokeObjectURL(u)); return []; });
        getMsg()?.success("Creado");
      }
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      await fetchFinanzas(latestFiltersRef.current, page);
    } catch (error: any) {
      console.error("Error en submit:", error);

      // Round 2026-05-01 — fix crítico: este toast no se renderizaba
      // porque `message.error` de antd estático no funciona en v5 + React
      // 19. Resultado: cuando la creación de un sueldo fallaba (validación
      // del Form, 400 del backend, etc.) el modal se quedaba abierto sin
      // mensaje, dando la impresión de que "el botón Crear no funciona".
      // Con el bridge ahora SÍ se ve el mensaje y el usuario sabe qué corregir.
      // Filtramos `errorFields` (validateFields lanza con esa shape cuando
      // hay campos inválidos) — antd ya pinta el error inline bajo cada
      // campo, no tiene sentido toastear "X is required" duplicado.
      const isValidationError =
        error && typeof error === "object" && Array.isArray(error.errorFields);
      if (!isValidationError) {
        getMsg()?.error(
          error?.response?.data?.message ||
            error?.message ||
            "Ocurrió un error al guardar",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Round 2026-04-30 — Esporádicos primero por default. El usuario reportó
  // que la mayoría de los movimientos del día a día son esporádicos
  // (gastos puntuales, sueldos extra) y que abrir la pestaña en
  // "Regulares" obligaba a un click extra siempre. Cambiar el orden
  // alcanza para que el primer tab visible sea esporádicos sin tener
  // que setear `defaultActiveKey` (AntD usa el primer item por default).
  const subTabs = [
    { key: "esporadico" as const, label: "Esporádicos" },
    { key: "regular" as const, label: "Regulares" },
  ];

  // La pestaña Esporádicos/Regulares parte la tabla en dos: es un filtro y se
  // recuerda. Antes la manejaba AntD sola (siempre abría en Esporádicos).
  const [subTab, setSubTab] = useFiltroPersistido<"esporadico" | "regular">(
    `${clave}.periodicidad`,
    "esporadico",
    { validar: (v) => v === "esporadico" || v === "regular" },
  );

  const mapUiToApi = (uiFilters: Record<string, any>, page: number) => {
    const fields: any = {};

    const contains = (keyUi: string, fieldDb: string) => {
      if (uiFilters[keyUi]) {
        fields[fieldDb] = { contains: uiFilters[keyUi] };
      }
    };

    const eqOrIn = (key: string) => {
      const v = fields[key];
      if (v == null) return;

      if (Array.isArray(v)) {
        if (v.length === 0) return;
        fields[key] = { in: v };
      } else {
        fields[key] = { eq: v };
      }
    };

    // Mapeo de filtros "contains"
    contains("nombre", "nombre");
    contains("rolAF", "rol_de_aloja_facil");
    contains("observaciones", "descripcion");

    // eqOrIn("pagado_por");
    // eqOrIn("pagado_a");

    if (uiFilters.monto != null) {
      const min =
        uiFilters.monto.min != null ? Number(uiFilters.monto.min) : undefined;
      const max =
        uiFilters.monto.max != null ? Number(uiFilters.monto.max) : undefined;

      if (min != null && max != null) {
        fields.monto = { between: [min, max] };
      } else if (min != null) {
        fields.monto = { gte: min };
      } else if (max != null) {
        fields.monto = { lte: max };
      }
    }

    // Filtro de propiedades e ID
    if (uiFilters.propiedadId != null) {
      fields.propiedad_id = { eq: uiFilters.propiedadId };
    }

    // Filtros adicionales como categoría, imagenes, etc.
    if (Array.isArray(uiFilters.categoria) && uiFilters.categoria.length > 0) {
      fields.categoria = { in: uiFilters.categoria };
    } else if (
      uiFilters.categoria != null &&
      !Array.isArray(uiFilters.categoria)
    ) {
      fields.categoria = { eq: uiFilters.categoria };
    }

    if (uiFilters.imagenes != null) {
      fields.imagenes = { eq: uiFilters.imagenes };
    }

    if (uiFilters.pagadoA != null)
      fields.pagado_a_usuario_id = uiFilters.pagadoA;
    if (uiFilters.pagadoPor != null)
      fields.pagado_por_usuario_id = uiFilters.pagadoPor;

    eqOrIn("pagado_a_usuario_id");
    eqOrIn("pagado_por_usuario_id");

    return {
      filters: { fields },
      page,
      // En modo readOnly (/finanzas-empresa) traemos hasta 200 registros del
      // período en una sola página para que los KPIs (Σ Ingresos AF, Σ Egresos
      // AF, Neto) y la tabla detallada operen sobre el set completo. Sin esto
      // el card mostraría sumas truncadas a los primeros 20 registros del
      // período. El backend ya soporta page_size hasta 200 (clamp).
      page_size: readOnly ? 200 : 20,
    };
  };

  const buildGlobalRange = () => {
    const ff = filtroFecha ?? { modo: "mes", mes: ahoraChile() };

    if (ff.modo === "mes" || ff.modo === "anterior") {
      const m = ff.mes ?? ahoraChile();
      return { from: m.startOf("month"), to: m.endOf("month") };
    }
    if (ff.modo === "rango" && ff.rango) {
      return { from: ff.rango[0].startOf("day"), to: ff.rango[1].endOf("day") };
    }
    // fallback final
    const m = ahoraChile();
    return { from: m.startOf("month"), to: m.endOf("month") };
  };

  const fetchFinanzas = useCallback(
    async (uiFilters: Record<string, any> = {}, newPage = 1) => {
      try {
        setLoading(true);
        const globalRange = buildGlobalRange();
        const globalDateRange = globalRange
          ? [
              globalRange.from.format("YYYY-MM-DD"),
              globalRange.to.format("YYYY-MM-DD"),
            ]
          : undefined;

        const body = {
          ...mapUiToApi(uiFilters, newPage),
          globalDateRange,
          page: newPage,
        };
        body.page = newPage;
        const endpoint =
          titulo === "Sueldos"
            ? "/finanzas/empresa/sueldos/search"
            : "/finanzas/empresa/gastos/search";

        const json = await apiFetch<{ data: any[]; total: number }>(endpoint, {
          method: "POST",
          body: JSON.stringify(body),
        });
        if (!json) return;
        setData(json.data);
        setTotal(json.total);
      } catch (err: any) {
        getMsg()?.error(err.message || "Error al cargar finanzas");
      } finally {
        setLoading(false);
      }
    },
    [page, titulo, filtroFecha],
  );

  useEffect(() => {
    // Con los filtros recordados, la primera consulta tiene que salir ya
    // filtrada: si no, el panel muestra un filtro aplicado y la tabla trae
    // todo el período.
    fetchFinanzas(filters, 1);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchFinanzas(filters, 1);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroFecha, refreshKey]);

  const latestFiltersRef = React.useRef(filters);
  useEffect(() => {
    latestFiltersRef.current = filters;
  }, [filters]);

  const categorias = [
    "reparacion",
    "cuenta",
    "aseo",
    "compra",
    "movimiento",
    "transporte",
    "emergencia",
    "publicidad",
    "arriendo",
    "sueldo",
    "computacion",
    "fotografia",
    // "lavanderia" NO va acá (round 2026-07-26): la plata de lavandería se
    // registra en /lavanderia, sobre la tabla `lavados`, porque el cierre suma
    // los costos reales desde ahí. Ofrecerla como categoría de gasto invitaba
    // a registrar el costo en `gastos` con un receptor externo llamado
    // "Lavandería X" — invisible para la reconciliación y con doble conteo en
    // la utilidad. La lista queda alineada con FLUJO_CATEGORIAS de /flujos.
    "supervision",
    "impuestos",
    "reembolso",
    "otros",
  ];

  const categoriasFiltradas = categorias.filter((c) => c !== "sueldo");

  // El mapeo de labels vive a nivel módulo (CATEGORIA_LABELS) para que
  // tanto la columna "Categoría" de la tabla como las opciones del
  // Select del modal usen exactamente los mismos textos.
  const categoriaOptions = categoriasFiltradas.map((c) => ({
    value: c,
    label: CATEGORIA_LABELS[c] ?? c.charAt(0).toUpperCase() + c.slice(1),
  }));

  return (
    <div className="finanzas-section">
      <div className="toolbar">
        {/*
          Buscador rápido client-side. Filtra contra los campos del row
          (nombre, descripción, pagado a/por, propiedad, monto, fecha) en
          la página actual de resultados. Para búsquedas que requieran
          filtros estructurados o paginar todo el dataset, usar "Filtro
          avanzado" a la derecha.
        */}
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          placeholder={`Buscar ${titulo.toLowerCase()} por nombre, descripción, propiedad…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ maxWidth: 360, marginRight: 12 }}
        />
        <Filtros
          persistenceKey={"sueldos-gastos"}
          value={filters}
          onApply={(appliedFilters) => {
            setFilters(appliedFilters);
            setPage(1);
            fetchFinanzas(appliedFilters, 1);
          }}
          items={[
            {
              key: "nombre",
              type: "text",
              label: "Nombre",
            },
            {
              key: "pagadoA",
              type: "select",
              label: "Pagado a",
              mode: "multiple",
              allowSearch: true,
              options: usuarios.map((u) => ({ value: u.id, label: u.nombre })),
            },
            {
              key: "pagadoPor",
              type: "select",
              label: "Pagado por",
              mode: "multiple",
              allowSearch: true,
              options: usuarios.map((u) => ({ value: u.id, label: u.nombre })),
            },
            // En Sueldos no hay propiedad asociada — se omite el filtro
            // (round 2026-04-30).
            ...(titulo !== "Sueldos"
              ? ([
                  {
                    key: "propiedadId",
                    type: "select" as const,
                    label: "Propiedades",
                    allowSearch: true,
                    options: propiedades.map((p) => ({
                      value: p.id,
                      label: formatPropertyName(p.nombre),
                    })),
                  },
                ] as const)
              : []),
            {
              key: "rolAF",
              type: "select",
              label: "Rol de Aloja Fácil",
              allowSearch: true,
              options: ["intermediario", "recibe", "paga"].map((v) => ({
                value: v,
                label:
                  v === "intermediario"
                    ? "Intermediario"
                    : v === "recibe"
                      ? "Recibe"
                      : "Paga",
              })),
            },
            {
              key: "observaciones",
              type: "text",
              label: "Observaciones",
              placeholder: "observaciones...",
            },
            {
              key: "monto",
              type: "numberRange",
              label: "Monto",
            },
            ...(titulo === "Gastos"
              ? ([
                  {
                    key: "categoria",
                    type: "select" as const,
                    label: "Categoría",
                    mode: "multiple",
                    allowSearch: true,
                    options: categoriaOptions,
                  },
                ] as const)
              : []),
          ]}
        />
        <Button
          onClick={() => {
            const now = ahoraChile().format("YYYY-MM-DD_HH-mm");
            exportToXlsxMultiSheet({
              filename: `${titulo}_${now}.xlsx`,
              sheets: [
                {
                  sheetName: "Regulares",
                  rows: filtered.filter((r) => r.tipo === "regular"),
                },
                {
                  sheetName: "Esporádicos",
                  rows: filtered.filter((r) => r.tipo === "esporadico"),
                },
              ],
            });
          }}
        >
          Exportar a XLS
        </Button>
      </div>
      {/* Botón "Nuevo X" se oculta en modo solo-lectura. En `/finanzas-
          empresa` el dato se ingresa desde sus páginas dedicadas
          (/gastos, /sueldos), no desde el panel de reportes. */}
      {!readOnly && (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => onEdit()}>
          Nuevo {titulo.slice(0, -1)}
        </Button>
      )}
      {/*
        Barra contextual de borrado masivo. Solo aparece cuando el usuario
        seleccionó al menos una fila Y la sección no está en readOnly. En
        /finanzas-empresa esta barra nunca se ve porque rowSelection no
        se aplica a la Table cuando readOnly.
      */}
      {!readOnly && selectedRowKeys.length > 0 && (
        <div
          style={{
            marginTop: 12,
            padding: "8px 12px",
            background: "#fff1f0",
            border: "1px solid #ffa39e",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span>
            <strong>{selectedRowKeys.length}</strong> registro
            {selectedRowKeys.length === 1 ? "" : "s"} seleccionado
            {selectedRowKeys.length === 1 ? "" : "s"}
          </span>
          <Space>
            <Button
              onClick={() => setSelectedRowKeys([])}
              disabled={bulkDeleting}
            >
              Limpiar selección
            </Button>
            <Popconfirm
              title={`Eliminar ${selectedRowKeys.length} registro${selectedRowKeys.length === 1 ? "" : "s"}`}
              description={(() => {
                // Mismo aviso que el borrado individual: un regular no se borra
                // "desde ahora", se borra entero — meses ya pagados incluidos.
                const regulares = filtered.filter(
                  (r) => selectedRowKeys.includes(r.id) && r.tipo === "regular",
                ).length;
                return (
                  <div style={{ maxWidth: 340 }}>
                    Esta acción es irreversible. Los registros desaparecen del
                    reporte de finanzas.
                    {regulares > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <b>
                          {regulares} {regulares === 1 ? "es regular" : "son regulares"}
                        </b>
                        : se borran completos, incluidos los meses ya
                        transcurridos. Para detenerlos sin perder el historial,
                        usa «Terminar» en cada uno.
                      </div>
                    )}
                  </div>
                );
              })()}
              okText="Eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true, loading: bulkDeleting }}
              onConfirm={handleBulkDelete}
            >
              <Button danger icon={<DeleteOutlined />} loading={bulkDeleting}>
                Eliminar seleccionados
              </Button>
            </Popconfirm>
          </Space>
        </div>
      )}

      {/* Cola de revisión de la vigencia (round 2026-07-28, espeja la de
          /flujos). Los regulares creados antes de que existiera el rango no
          tienen desde-cuándo, y no hay de dónde inferirlo: la tabla `gastos`
          no guarda fecha de creación. El sistema NO se la inventa —siguen
          contando en todos los meses, igual que antes— y quedan listados acá
          para que un humano los acote. Solo en modo CRUD: en /finanzas-empresa
          la sección es puro reporte y no se puede arreglar nada desde ahí. */}
      {!readOnly &&
        (() => {
          const sinVigencia = filtered.filter(
            (r) => r.tipo === "regular" && !r.vigenciaInicio && !r.vigenciaFin,
          );
          if (sinVigencia.length === 0) return null;
          const uno = sinVigencia.length === 1;
          return (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${sinVigencia.length} registro${uno ? "" : "s"} regular${uno ? "" : "es"} sin vigencia registrada`}
              description={
                <>
                  Se crearon antes de que existieran las fechas de inicio y
                  término, así que <strong>cuentan en todos los meses</strong>,
                  pasados y futuros. Ábrelos con «Editar» y define desde qué mes
                  rigen (y hasta cuándo, si ya terminaron):{" "}
                  {sinVigencia
                    .slice(0, 6)
                    .map((r) => r.nombre || r.descripcion || `#${r.id}`)
                    .join(" · ")}
                  {sinVigencia.length > 6
                    ? ` … y ${sinVigencia.length - 6} más`
                    : ""}
                  .
                </>
              }
            />
          );
        })()}

      {readOnly ? (
        // Round 2026-05-12 — Modo PURO REPORTE (/finanzas-empresa):
        //   • Sin Tabs Esporádicos | Regulares (escondía la mitad de la data
        //     hasta hacer un click extra).
        //   • Una Card por tipo, apiladas verticalmente. Cada Card muestra:
        //       - 3 KPIs resumen (count + Σ monto + Σ del flujo principal).
        //       - Toggle "Ver detalle" → expande la tabla completa adentro
        //         de la misma Card.
        // En modo NO-readOnly (/gastos CRUD) seguimos usando Tabs, que es
        // más eficiente para el flujo de creación/edición rápida.
        <FinanzasReadOnlyCards
          rows={filtered}
          columns={columnsBase}
          loading={!!loading}
          subTabs={subTabs}
          seccion={titulo}
        />
      ) : (
        <Tabs
          activeKey={subTab}
          onChange={(k) => setSubTab(k as "esporadico" | "regular")}
          items={subTabs.map((t) => ({
            key: t.key,
            label: t.label,
            children: (
              <Table
                rowKey="id"
                loading={loading}
                dataSource={filtered.filter((r) => r.tipo === t.key)}
                columns={columnsBase}
                size="middle"
                pagination={paginacionCliente({ porDefecto: 10, etiqueta: "movimiento" })}
                scroll={{ x: 1250 }}
                rowSelection={{
                  selectedRowKeys,
                  onChange: (keys) => setSelectedRowKeys(keys),
                  preserveSelectedRowKeys: true,
                }}
              />
            ),
          }))}
        />
      )}

      {/* Terminar un regular — round 2026-07-28. Diálogo aparte y no un campo
          más del formulario grande porque es la acción cotidiana: «este sueldo
          ya no va». Dos clics, sin pasar por los 15 campos del alta. */}
      <Modal
        title={`Terminar «${terminando?.nombre ?? ""}»`}
        open={Boolean(terminando)}
        onCancel={() => setTerminando(null)}
        onOk={confirmarTerminar}
        okText="Terminar"
        confirmLoading={terminandoLoading}
        okButtonProps={{ disabled: !mesTermino }}
        destroyOnClose
      >
        <p style={{ marginTop: 0 }}>
          Elige el <b>último mes</b> en que se paga. Desde el mes siguiente deja
          de aparecer y de sumar en los reportes.
        </p>
        <DatePicker
          picker="month"
          style={{ width: "100%" }}
          format="MM/YYYY"
          placeholder="Último mes"
          value={mesTermino}
          onChange={(v) => setMesTermino(v)}
        />
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="No se borra nada"
          description="Los meses ya transcurridos quedan igual: el registro sigue existiendo y su historial también. Solo deja de regir de ahí en adelante. Si en vez de detenerlo necesitas cambiarle el monto, termínalo este mes y crea uno nuevo desde el siguiente — así el pasado conserva el monto que realmente se pagó."
        />
      </Modal>

      <Modal
        title={
          editing
            ? `Editar ${titulo.slice(0, -1)}`
            : `Nuevo ${titulo.slice(0, -1)}`
        }
        open={modalOpen}
        onCancel={() => {
          if (submitting) return; // no cerrar mientras guardamos
          setModalOpen(false);
          setEditing(null);
          setPendingPreviews((prev) => { prev.forEach((u) => URL.revokeObjectURL(u)); return []; });
          setPendingImages([]);
        }}
        onOk={submit}
        okText={editing ? "Guardar" : "Crear"}
        // Spinner + bloqueo del botón OK durante el submit. Junto con el guard
        // dentro de `submit()` evita los doble-clicks que creaban duplicados.
        confirmLoading={submitting}
        okButtonProps={{ disabled: submitting }}
        cancelButtonProps={{ disabled: submitting }}
        maskClosable={!submitting}
        keyboard={!submitting}
        // Modal más ancho (round 2026-04-30): el usuario reportó que era "muy
        // vertical y cabría en horizontal". Con 1100 + grid de 3 columnas
        // entran tipo/categoría/nombre/monto/fecha/propiedad en una pantalla
        // estándar sin scroll. Descripción e imágenes ocupan full-width.
        width={1100}
        // top fijo en vez de centrado (round 2026-04-30 bis): el centrado
        // hace que el modal flote en medio de la pantalla, y cuando el
        // contenido (Form + miniaturas + zona drag&drop) supera la altura
        // disponible, el body desbordaba HACIA ABAJO, fuera de la caja blanca
        // del modal — el footer (Cancelar/Guardar) quedaba montado sobre la
        // zona drag&drop y la zona se veía "fuera del modal". Solución:
        // anclamos el modal arriba (top: 24) y limitamos la altura del body
        // con `maxHeight: calc(100vh - 180px)` + scroll interno. Así el body
        // hace scroll si es necesario y el footer queda siempre pegado al
        // borde inferior del modal blanco.
        centered={false}
        style={{ top: 24 }}
        styles={{
          body: {
            maxHeight: "calc(100vh - 180px)",
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: 8, // hueco para el scrollbar sin solapar contenido
          },
        }}
      >
        <Form form={form} layout="vertical">
          {/*
            Sección 1 — Identificación del gasto.
            Round 2026-04-30 — el usuario reportó que los campos estaban
            desordenados. Ahora los agrupamos en 3 secciones lógicas:
              1) Identificación (qué gasto es): tipo, categoría, nombre,
                 monto, fecha, propiedad.
              2) Detalles del pago: rol AF + pagado por/a (con sync
                 automático entre los tres campos — ver más abajo).
              3) Descripción + adjuntos.
            Cada sección tiene su propio grid de 3 (o 2) columnas para
            evitar el caos del grid plano de antes, donde campos
            condicionales hacían saltos visuales.
          */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "0 16px",
            }}
          >
            <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: "esporadico", label: "Esporádico" },
                  { value: "regular", label: "Regular" },
                ]}
              />
            </Form.Item>
            {titulo !== "Sueldos" && (
              <Form.Item
                name="categoria"
                label="Categoría"
                rules={[
                  { required: true, message: "La categoría es obligatoria" },
                ]}
              >
                <Select
                  options={categoriaOptions}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  // Comportamiento al cambiar de categoría:
                  //
                  // 1) Si la nueva categoría restringe los roles válidos en
                  //    "Pagado a" y el rol actualmente seleccionado quedó
                  //    fuera de la whitelist, lo limpiamos junto con sus
                  //    sub-campos (usuario / receptor) y resincronizamos el
                  //    rolDeAlojaFacil para mantener coherencia.
                  //
                  // 2) Caso "reembolso" — además del filtrado, aplicamos el
                  //    default semántico: AF pagó al trabajador que compró
                  //    en su nombre. Por eso pagadoPor = administrador y
                  //    pagadoA = trabajador. El usuario sigue pudiendo
                  //    cambiar pagadoA a "aseador" si corresponde. Solo se
                  //    aplica si los campos están vacíos (no pisamos lo
                  //    elegido a mano por el admin).
                  onChange={(nuevaCategoria) => {
                    const rolesValidos = getRolesPagadoA(nuevaCategoria);
                    const rolActual = form.getFieldValue("pagadoARol");
                    if (
                      rolActual &&
                      !rolesValidos.some((r) => r.value === rolActual)
                    ) {
                      form.setFieldValue("pagadoARol", undefined);
                      form.setFieldValue("pagadoAUsuarioId", null);
                      form.setFieldValue("pagadoARecipientId", null);
                      syncRolAFFromPagados();
                    }
                    if (nuevaCategoria === "reembolso") {
                      if (!form.getFieldValue("pagadoPorRol")) {
                        form.setFieldValue("pagadoPorRol", "administrador");
                        form.setFieldValue("pagadoPorUsuarioId", null);
                        form.setFieldValue("pagadoPorRecipientId", null);
                      }
                      if (!form.getFieldValue("pagadoARol")) {
                        form.setFieldValue("pagadoARol", "trabajador");
                        form.setFieldValue("pagadoAUsuarioId", null);
                        form.setFieldValue("pagadoARecipientId", null);
                      }
                      syncRolAFFromPagados();
                    }
                  }}
                />
              </Form.Item>
            )}
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[{ required: true, message: "Ingresa un nombre" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="monto"
              label="Monto"
              rules={[
                { required: true, message: "Es obligatorio ingresar el monto" },
              ]}
            >
              <InputNumber formatter={clpInputFormatter} parser={clpInputParser} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item shouldUpdate noStyle>
              {() => {
                const tipo = form.getFieldValue("tipo");
                return tipo === "regular" ? (
                  <Form.Item
                    name="fechaPeriodica"
                    label="Fecha (día del mes)"
                    rules={[
                      {
                        required: true,
                        message: "Ingresa el día del mes. Ej: 10",
                      },
                    ]}
                  >
                    <Input placeholder="Ej: 5" />
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="fecha"
                    label="Fecha"
                    rules={[
                      { required: true, message: "Ingresa la fecha" },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD-MM-YYYY"
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            {/*
              VIGENCIA de un flujo regular (round 2026-07-28 acá; el mecanismo
              es el de /flujos, round 2026-07-26).

              Por qué hace falta: un regular es UNA fila que vale por N meses.
              Sin un rango, editarlo cambia el pasado y borrarlo lo borra
              entero — que es exactamente lo que reportó el dueño de los
              sueldos: «si quiero eliminar o detener uno no puedo, se me borra
              todo el pasado». Con vigencia, detener un sueldo es ponerle
              término, y subirlo es cerrar el viejo y abrir uno nuevo desde el
              mes siguiente: el historial queda intacto.

              Granularidad MES, igual que en /flujos: un regular se paga un día
              fijo del mes, así que "vigente desde el 15" no tendría respuesta
              clara para ese mes.
            */}
            <Form.Item shouldUpdate noStyle>
              {() => {
                if (form.getFieldValue("tipo") !== "regular") return null;
                return (
                  <>
                    <Form.Item
                      name="vigenciaInicio"
                      label="Vigente desde (mes)"
                      tooltip="Desde qué mes empieza a pagarse. Antes de ese mes no aparece ni suma en ningún reporte."
                      rules={[
                        { required: true, message: "Indica desde qué mes rige" },
                      ]}
                    >
                      <DatePicker
                        picker="month"
                        style={{ width: "100%" }}
                        format="MM/YYYY"
                        placeholder="Mes de inicio"
                      />
                    </Form.Item>
                    <Form.Item
                      name="vigenciaFin"
                      label="Vigente hasta (mes, opcional)"
                      tooltip="Último mes en que se paga. Déjalo vacío si sigue indefinidamente; puedes ponerle término más adelante sin perder los meses ya pagados."
                      dependencies={["vigenciaInicio"]}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const ini = getFieldValue("vigenciaInicio");
                            if (!value || !ini) return Promise.resolve();
                            return value.isBefore(ini, "month")
                              ? Promise.reject(
                                  new Error(
                                    "El término no puede ser anterior al inicio",
                                  ),
                                )
                              : Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <DatePicker
                        picker="month"
                        style={{ width: "100%" }}
                        format="MM/YYYY"
                        placeholder="Sin término (indefinido)"
                      />
                    </Form.Item>
                  </>
                );
              }}
            </Form.Item>

            {/*
              PROPIEDAD — se omite en Sueldos por pedido del usuario
              (round 2026-04-30): un sueldo a un trabajador o aseador no
              está asociado a una propiedad específica. Se podría
              ofrecer como opcional pero la decisión es eliminarlo
              completamente para evitar guardar datos sin sentido.
              En Gastos se mantiene porque sí tiene sentido (ej. una
              reparación de una propiedad puntual).
            */}
            {titulo !== "Sueldos" && (
              <Form.Item name="propiedadId" label="Propiedad">
                <Select
                  showSearch
                  allowClear
                  placeholder="Buscar propiedad por nombre"
                  optionFilterProp="label"
                  options={propiedades.map((p) => ({
                    value: p.id,
                    label: formatPropertyName(p.nombre),
                  }))}
                />
              </Form.Item>
            )}
          </div>

          {/* Sección 2 — Detalles del pago */}
          <Divider
            orientation="left"
            plain
            style={{ marginTop: 0, marginBottom: 8 }}
          >
            Detalles del pago
          </Divider>

          {titulo === "Sueldos" && (
            // ── Alert explicativo del flujo financiero del sueldo ──────
            //
            // Round 2026-05-13 — Los sueldos siempre son egreso de AF
            // (rol "paga", pagadoPor = Aloja Fácil). El usuario pidió
            // explícitamente que el modal aclare que esto representa
            // SIEMPRE una pérdida de dinero para la empresa, y que la
            // única decisión es a quién se le paga.
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Los sueldos son siempre egreso de Aloja Fácil"
              description={
                <div style={{ fontSize: 13 }}>
                  Cada sueldo registrado representa una <b>pérdida de dinero</b> para
                  la empresa — AF paga, otro recibe. Por eso este modal solo te
                  permite configurar <b>a quién</b> se le paga (rol y persona).
                  El origen del pago siempre es Aloja Fácil y el flujo siempre
                  resta del neto de la empresa.
                </div>
              }
            />
          )}

          {/*
            Rol de Aloja Fácil — pivote. Cuando cambia, sincroniza
            pagadoPorRol / pagadoARol vía syncPagadosFromRolAF. La regla:
              • paga          → pagadoPor = administrador (locked)
              • recibe        → pagadoA   = administrador (locked)
              • intermediario → ningún lado puede ser administrador
            Required porque el resto del formulario depende de esto.

            En /sueldos (titulo === "Sueldos") los campos rolDeAlojaFacil
            y pagadoPorRol viven como Form.Item hidden con valores fijos
            ("paga" / "administrador") — el usuario no puede modificarlos.
            Razón: un sueldo POR DEFINICIÓN es egreso de AF.
          */}
          {titulo === "Sueldos" ? (
            <>
              <Form.Item name="rolDeAlojaFacil" hidden initialValue="paga">
                <Input type="hidden" />
              </Form.Item>
              <Form.Item name="pagadoPorRol" hidden initialValue="administrador">
                <Input type="hidden" />
              </Form.Item>
            </>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "0 16px",
              }}
            >
              <Form.Item
                name="rolDeAlojaFacil"
                label={
                  <Space size={4}>
                    <span>Rol de Aloja Fácil</span>
                    <Tooltip title="Define el flujo del dinero. 'Paga' = AF es el origen; 'Recibe' = AF es el destino; 'Intermediario' = AF está en el medio. Ajusta automáticamente Pagado por/a.">
                      <InfoCircleOutlined style={{ color: "#1677ff" }} />
                    </Tooltip>
                  </Space>
                }
                rules={[
                  { required: true, message: "Selecciona el rol de Aloja Fácil" },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={[
                    { value: "paga", label: "Paga" },
                    { value: "recibe", label: "Recibe" },
                    { value: "intermediario", label: "Intermediario" },
                  ]}
                  onChange={(val) =>
                    syncPagadosFromRolAF(
                      val as "paga" | "recibe" | "intermediario",
                    )
                  }
                />
              </Form.Item>
            </div>
          )}

          {/*
            Pagado por (izq) | Pagado a (der). Cada columna es un stack
            vertical: rol + sub-campo condicional (receptor o usuario)
            según el rol elegido. Antes el grid plano de 3 cols mezclaba
            rol-A con sub-de-por o rol-de-A con sub-de-por según qué
            condicionales se renderizaban.

            En /sueldos (titulo === "Sueldos") la columna "Pagado por" se
            colapsa: el origen siempre es Aloja Fácil. Por eso usamos
            grid de 1 columna en sueldos y de 2 en flujos genéricos. Los
            Form.Items de pagadoPor* se omiten completamente — el payload
            los obtiene de los hidden inputs declarados arriba y, además,
            la lógica de createSueldo en useFinanzasEmpresaLogic los
            normaliza por defense-in-depth.
          */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: titulo === "Sueldos" ? "1fr" : "1fr 1fr",
              gap: "0 16px",
            }}
          >
            {/* Columna izquierda — Pagado por (oculta en Sueldos) */}
            {titulo !== "Sueldos" && <div>
              {/*
                Round 2026-04-30 decies — opciones SIEMPRE habilitadas. El
                usuario pidió expresamente que todas las opciones estén
                disponibles en pagadoPor / pagadoA (corrección sobre la
                versión anterior que las disabilitaba según el Rol AF).
                La sincronización pasa SOLO por:
                  • Cuando rolAF cambia: autofill del lado correspondiente
                    (paga → pagadoPor=administrador; recibe → pagadoA=admin).
                  • Cuando pagadoPor/A cambia: recálculo automático de
                    rolAF (sync inverso).
                El usuario puede picar cualquier rol en cualquier momento;
                el sistema mantiene la coherencia recalculando rolAF.
              */}
              <Form.Item
                name="pagadoPorRol"
                label={
                  <Space size={4}>
                    <span>Pagado por (rol)</span>
                    <Popover
                      title="Receptores externos"
                      content={
                        <div style={{ maxWidth: 360 }}>
                          <Typography.Paragraph
                            style={{
                              whiteSpace: "pre-line",
                              marginBottom: 0,
                            }}
                          >
                            {RECEPTOR_INFO_TEXT}
                          </Typography.Paragraph>
                        </div>
                      }
                      trigger="hover"
                    >
                      <InfoCircleOutlined style={{ color: "#1677ff" }} />
                    </Popover>
                  </Space>
                }
                rules={[{ required: true }]}
              >
                <Select
                  options={ROLES}
                  placeholder="Selecciona rol"
                  onChange={() => {
                    form.setFieldValue("pagadoPorUsuarioId", null);
                    form.setFieldValue("pagadoPorRecipientId", null);
                    syncRolAFFromPagados();
                  }}
                />
              </Form.Item>

              {/* Receptor externo (tipo 5) — solo cuando pagadoPorRol === "receptor_externo" */}
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const rol = form.getFieldValue("pagadoPorRol");
                  if (rol !== "receptor_externo") return null;
                  return (
                    <Form.Item
                      name="pagadoPorRecipientId"
                      label="Pagado por (receptor)"
                      rules={[
                        { required: true, message: "Selecciona un receptor" },
                      ]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="Selecciona receptor externo"
                        options={recipientOptions}
                        dropdownRender={(menu) => (
                          <>
                            {menu}
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                padding: 8,
                                borderTop: "1px solid #f0f0f0",
                              }}
                            >
                              <Button
                                size="small"
                                icon={<PlusOutlined />}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => openRecipientsManager("create")}
                              >
                                Crear receptor
                              </Button>
                              <Button
                                size="small"
                                icon={<SettingOutlined />}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => openRecipientsManager("list")}
                              >
                                Gestionar
                              </Button>
                            </div>
                          </>
                        )}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>

              <Form.Item noStyle shouldUpdate>
                {() => {
                  const rol = form.getFieldValue("pagadoPorRol") as
                    | Rol
                    | undefined;
                  // Roles que NO requieren elegir un usuario específico:
                  //   • administrador → siempre Aloja Fácil (entidad jurídica)
                  //   • arrendatario  → huésped, no es un User del sistema
                  //   • receptor_externo → entidad no-User (otra rama abajo)
                  // El resto (propietario / aseador / trabajador) sí permiten
                  // elegir un usuario específico — round 2026-04-30 decies.
                  if (
                    !rol ||
                    rol === "administrador" ||
                    rol === "arrendatario" ||
                    rol === "receptor_externo"
                  )
                    return null;
                  const list = usuariosPorRol.get(rol) || [];
                  return (
                    <Form.Item
                      name="pagadoPorUsuarioId"
                      label="Pagado por (usuario)"
                      rules={[
                        { required: true, message: "Selecciona un usuario" },
                      ]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder={
                          rol ? `Usuarios rol: ${rol}` : "Primero elige un rol"
                        }
                        // Round 2026-05-09: label "Nombre — Propiedad" via
                        // helper compartido. El backend (getUsersPerRol)
                        // devuelve `propiedades: string[]` por usuario;
                        // si está vacío (aseador/trabajador/admin sin
                        // propiedad asociada) el helper omite el sufijo.
                        options={list.map(userSelectOption)}
                        dropdownRender={(menu) => {
                          // Inline-create solo para rol "trabajador" — los
                          // aseadores/propietarios se crean desde sus
                          // módulos dedicados.
                          if (rol !== "trabajador") return menu;
                          return (
                            <>
                              {menu}
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  padding: 8,
                                }}
                              >
                                <Input
                                  placeholder="Nuevo trabajador..."
                                  onPressEnter={async (e) => {
                                    const nombre = (
                                      e.currentTarget.value || ""
                                    ).trim();
                                    if (!nombre) return;
                                    const nuevo = await crearTrabajador(nombre);
                                    setUsuariosLocal((prev) => [
                                      ...prev,
                                      nuevo,
                                    ]);
                                    form.setFieldValue(
                                      "pagadoPorUsuarioId",
                                      nuevo.id,
                                    );
                                    e.currentTarget.value = "";
                                    getMsg()?.success("Trabajador creado");
                                  }}
                                />
                              </div>
                            </>
                          );
                        }}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </div>}

            {/* Columna derecha — Pagado a (en /sueldos pasa a ser la única) */}
            <div>
              {/* Mismo criterio que pagadoPorRol: todas las opciones
                  siempre habilitadas. Round 2026-04-30 decies.
                  EXCEPCIÓN (round 2026-05-12): si categoria === "reembolso"
                  filtramos las opciones a aseador/trabajador solamente.
                  Por eso este Form.Item va dentro de un shouldUpdate que
                  observa `categoria` y recalcula las opciones. */}
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev.categoria !== curr.categoria
                }
              >
                {() => {
                  const categoriaActual = form.getFieldValue("categoria");
                  const rolesPagadoA = getRolesPagadoA(categoriaActual);
                  return (
                    <Form.Item
                      name="pagadoARol"
                      label={
                        <Space size={4}>
                          <span>Pagado a (rol)</span>
                          <Popover
                            title="Receptores externos"
                            content={
                              <div style={{ maxWidth: 360 }}>
                                <Typography.Paragraph
                                  style={{
                                    whiteSpace: "pre-line",
                                    marginBottom: 0,
                                  }}
                                >
                                  {RECEPTOR_INFO_TEXT}
                                </Typography.Paragraph>
                              </div>
                            }
                            trigger="hover"
                          >
                            <InfoCircleOutlined
                              style={{ color: "#1677ff" }}
                            />
                          </Popover>
                        </Space>
                      }
                      rules={[{ required: true }]}
                    >
                      <Select
                        options={rolesPagadoA}
                        placeholder="Selecciona rol"
                        onChange={() => {
                          form.setFieldValue("pagadoAUsuarioId", null);
                          form.setFieldValue("pagadoARecipientId", null);
                          syncRolAFFromPagados();
                        }}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>

              {/* Receptor externo (tipo 5) — solo cuando pagadoARol === "receptor_externo" */}
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const rol = form.getFieldValue("pagadoARol");
                  if (rol !== "receptor_externo") return null;
                  return (
                    <Form.Item
                      name="pagadoARecipientId"
                      label="Pagado a (receptor)"
                      rules={[
                        { required: true, message: "Selecciona un receptor" },
                      ]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="Selecciona receptor externo"
                        options={recipientOptions}
                        dropdownRender={(menu) => (
                          <>
                            {menu}
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                padding: 8,
                                borderTop: "1px solid #f0f0f0",
                              }}
                            >
                              <Button
                                size="small"
                                icon={<PlusOutlined />}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => openRecipientsManager("create")}
                              >
                                Crear receptor
                              </Button>
                              <Button
                                size="small"
                                icon={<SettingOutlined />}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => openRecipientsManager("list")}
                              >
                                Gestionar
                              </Button>
                            </div>
                          </>
                        )}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>

              <Form.Item shouldUpdate noStyle>
                {() => {
                  const rol = form.getFieldValue("pagadoARol");
                  // Mismo criterio que pagadoPor (round 2026-04-30 decies):
                  // propietario / aseador / trabajador permiten elegir un
                  // usuario específico de la lista.
                  if (
                    !rol ||
                    rol === "administrador" ||
                    rol === "arrendatario" ||
                    rol === "receptor_externo"
                  )
                    return null;
                  const usuariosList = usuariosPorRol.get(rol) || [];
                  return (
                    <Form.Item
                      name="pagadoAUsuarioId"
                      label="Pagado a (usuario)"
                      rules={[
                        { required: true, message: "Selecciona un usuario" },
                      ]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder={
                          rol
                            ? `Usuarios con rol: ${rol}`
                            : "Primero selecciona un rol"
                        }
                        // Round 2026-05-09: ver comentario homólogo en
                        // "Pagado por (usuario)" arriba.
                        options={usuariosList.map(userSelectOption)}
                        dropdownRender={(menu) =>
                          // Inline-create solo para rol "trabajador" — los
                          // aseadores/propietarios se crean desde sus
                          // módulos dedicados.
                          rol !== "trabajador" ? (
                            menu
                          ) : (
                            <>
                              {menu}
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  padding: 8,
                                  borderTop: "1px solid #f0f0f0",
                                }}
                              >
                                <Input
                                  placeholder="Nuevo trabajador..."
                                  onPressEnter={async (e) => {
                                    const nombre =
                                      e.currentTarget.value.trim();
                                    if (!nombre) return;
                                    try {
                                      const nuevo =
                                        await crearTrabajador(nombre);
                                      setUsuariosLocal((prev) => [
                                        ...prev,
                                        nuevo,
                                      ]);
                                      form.setFieldValue(
                                        "pagadoAUsuarioId",
                                        nuevo.id,
                                      );
                                      e.currentTarget.value = "";
                                      getMsg()?.success("Trabajador creado");
                                    } catch {
                                      getMsg()?.error(
                                        "Error al crear trabajador",
                                      );
                                    }
                                  }}
                                />
                              </div>
                            </>
                          )
                        }
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </div>
          </div>

          {/* Sección 3 — Descripción y adjuntos */}
          <Divider
            orientation="left"
            plain
            style={{ marginTop: 0, marginBottom: 8 }}
          >
            Detalle adicional
          </Divider>

          <Form.Item name="descripcion" label="Descripción">
            <Input.TextArea rows={3} />
          </Form.Item>

          <div>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
              Imágenes
            </label>
            {/*
              Grid CSS con minmax(80px, 1fr): cada miniatura ocupa al menos
              80px y se acomoda en filas. Antes era flex con gap 8 y el botón
              de eliminar (top:-6, right:-6) se montaba sobre la siguiente
              imagen, además de quedar tapado por el ex-botón "Editar imagen".
              Ahora el botón Eliminar va DENTRO de la miniatura (esquina sup.
              derecha con un pelo de margen) y nunca se solapa con vecinas.
            */}
            {(expenseImages.length > 0 || (!editing && pendingPreviews.length > 0)) && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                {expenseImages.map((adj) => (
                  <div
                    key={adj.url}
                    style={{
                      position: "relative",
                      width: 80,
                      height: 80,
                      borderRadius: 4,
                      overflow: "visible",
                    }}
                  >
                    {/* Miniatura en el tile; la vista ampliada abre la foto completa. */}
                    <TileAdjunto
                      adjunto={adj}
                      size={80}
                      mascara="Ver"
                      onAbrirVideo={setVideoAbierto}
                    />
                    <Button
                      danger
                      size="small"
                      shape="circle"
                      icon={<DeleteOutlined />}
                      style={{ position: "absolute", top: 4, right: 4, zIndex: 3 }}
                      onClick={async () => {
                        if (!editing) return;
                        const prev = expenseImages;
                        setExpenseImages(prev.filter((a) => a.url !== adj.url));
                        // Por `key` cuando la hay: no depende de que la URL firmada siga vigente.
                        const query = adj.key
                          ? `key=${encodeURIComponent(adj.key)}`
                          : `url=${encodeURIComponent(adj.url)}`;
                        const json = await apiFetch(
                          `/finanzas/empresa/expenses/${editing.id}/images?${query}`,
                          { method: "DELETE" },
                        );
                        if (!json) {
                          setExpenseImages(prev);
                        }
                      }}
                    />
                  </div>
                ))}
                {!editing &&
                  pendingPreviews.map((url, i) => (
                    <div
                      key={`pending-${i}`}
                      style={{
                        position: "relative",
                        width: 80,
                        height: 80,
                        borderRadius: 4,
                      }}
                    >
                      <img
                        src={url}
                        alt="pending"
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                          borderRadius: 4,
                        }}
                      />
                      <Button
                        danger
                        size="small"
                        shape="circle"
                        icon={<DeleteOutlined />}
                        style={{ position: "absolute", top: 4, right: 4, zIndex: 3 }}
                        onClick={() => {
                          URL.revokeObjectURL(url);
                          setPendingPreviews((prev) => prev.filter((_, j) => j !== i));
                          setPendingImages((prev) => prev.filter((_, j) => j !== i));
                        }}
                      />
                    </div>
                  ))}
              </div>
            )}
            {/*
              Zona drag & drop + paste compartida del proyecto. Acepta el
              mismo formato que el ex-botón Editar imagen (image/* y video/*).
              El paste por Ctrl+V se ata via useClipboardPaste arriba.
              Round 2026-04-30: el botón "Editar imagen" se quitó porque no
              se usaba (los usuarios no editan, suben capturas crudas).
            */}
            <DragDropUpload
              accept="image/*,video/*"
              multiple
              loading={savingImage}
              onFile={(file) => {
                void addExpenseFile(file);
              }}
              compact
              title="Arrastra imágenes o videos aquí, o haz click para seleccionar"
              hint="También puedes pegar con Ctrl+V (captura de pantalla o archivo copiado)"
            />
          </div>
        </Form>
      </Modal>

      {/* Video de un adjunto: abre con su versión liviana si existe. */}
      <VisorVideoModal adjunto={videoAbierto} onClose={() => setVideoAbierto(null)} />

      <ExpenseRecipientsManager
        open={recipientsManagerOpen}
        initialMode={recipientsManagerInitialMode}
        onClose={() => setRecipientsManagerOpen(false)}
        onChange={handleRecipientsManagerChange}
      />
    </div>
  );
}

// ── Sub-componente: vista READ-ONLY de Cards apiladas ──────────────────────
//
// Round 2026-05-12 — usada en /finanzas-empresa (cuando `readOnly={true}`)
// para reemplazar la pestañera Tabs (Esporádicos | Regulares) por dos
// Cards verticales, alineado con el patrón de sectionLavanderia y
// sectionAseos. Cada Card muestra un KPI summary del grupo y permite
// expandir la tabla completa.
//
// El /gastos CRUD sigue usando la versión Tabs (más eficiente para
// crear/editar rápido sin scroll).

const fmtCLP = (v: number) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(v);

interface ReadOnlyCardProps {
  titulo: string;
  rows: RegistroFinanza[];
  columns: ColumnsType<RegistroFinanza>;
  loading: boolean;
  // Round 2026-05-13 — el card cambia de layout según el tipo de
  // sección:
  //   • "Gastos" (visible como "Flujos") — recuadro principal con
  //     Σ Ingresos AF / Σ Egresos AF / Neto AF + KPIs secundarios
  //     (counts e intermediario referencial).
  //   • "Sueldos" — recuadro principal con Σ Sueldos pagados
  //     (siempre pérdida AF) + KPIs secundarios (cantidad, promedio,
  //     desglose por destinatario). Los sueldos por definición son
  //     egreso AF y no tienen contraparte de ingreso, por eso el
  //     layout enfatiza "pérdida" en vez de "neto".
  // Si en el futuro aparece una tercera sección, agregar un branch
  // explícito; el fallback es el layout legacy (cantidad + Σ +
  // desglose por rolAF) que vivía acá antes.
  seccion: Seccion;
}

// Clasifica una fila como ingreso AF, egreso AF o intermediario según
// el rol y la presencia de paired_id. Convención (alineada con /flujos):
//   - intermediario  → rol === "intermediario" (legacy) o pairedId != null
//   - ingreso AF     → rol === "recibe" y NO es parte de un par
//   - egreso AF      → rol === "paga"    y NO es parte de un par
// Los flujos sin rol AF (legacy data sucia) caen en "otros".
type FlujoKind = "ingreso" | "egreso" | "intermediario" | "otros";
const clasificarFlujo = (r: RegistroFinanza): FlujoKind => {
  const isInter = r.rolDeAlojaFacil === "intermediario" || r.pairedId != null;
  if (isInter) return "intermediario";
  if (r.rolDeAlojaFacil === "recibe") return "ingreso";
  if (r.rolDeAlojaFacil === "paga") return "egreso";
  return "otros";
};

// Etiquetas legibles del destinatario del sueldo. Las claves son los
// valores que viven en `pagadoARol`. La key "sin_destino" es para
// filas legacy donde el rol no se pudo inferir desde `pagadoA.tipo`.
const DESTINATARIO_LABELS: Record<string, { label: string; color: string }> = {
  aseador: { label: "Aseadores", color: "blue" },
  trabajador: { label: "Trabajadores", color: "geekblue" },
  receptor_externo: { label: "Receptores externos", color: "purple" },
  propietario: { label: "Clientes", color: "magenta" },
  administrador: { label: "Aloja Fácil (auto-pago)", color: "default" },
  sin_destino: { label: "Sin destinatario", color: "default" },
};

// KPIs agregados para el layout de Sueldos. Sueldos siempre son egreso
// AF (rol="paga" fijo, ver normalizarSueldo en useFinanzasEmpresaLogic),
// por eso la suma total es directamente "pérdida AF en sueldos" del
// período. Desglosamos por destinatario (aseador / trabajador /
// receptor externo / etc.) para que el admin pueda ver dónde se está
// yendo el dinero de sueldos sin abrir la tabla detallada.
function calcStatsSueldos(rows: RegistroFinanza[]) {
  const total = rows.reduce((s, r) => s + (Number(r.monto) || 0), 0);
  const count = rows.length;
  const byDestinatario: Record<string, { count: number; monto: number }> = {};
  for (const r of rows) {
    // Preferimos `pagadoARol` (UI), pero si no existe inferimos desde
    // el `tipo` del PagoEntidad persistido en `pagadoA` para filas
    // legacy que no traían el campo UI.
    const tipo = r.pagadoA?.tipo;
    let key: string = r.pagadoARol ?? "";
    if (!key) {
      if (tipo === 3) key = "aseador";
      else if (tipo === 4) key = "trabajador";
      else if (tipo === 5) key = "receptor_externo";
      else if (tipo === 1) key = "propietario";
      else if (tipo === 0) key = "administrador";
      else key = "sin_destino";
    }
    if (!byDestinatario[key]) byDestinatario[key] = { count: 0, monto: 0 };
    byDestinatario[key].count += 1;
    byDestinatario[key].monto += Number(r.monto) || 0;
  }
  return {
    count,
    total,
    promedio: count > 0 ? Math.round(total / count) : 0,
    byDestinatario,
  };
}

// KPIs agregados para el layout de Flujos. Devuelve montos firmes que
// SÍ afectan el neto de AF (ingresos/egresos sin intermediarios) y
// montos referenciales (intermediado, contado UNA vez por par).
function calcStatsFlujos(rows: RegistroFinanza[]) {
  // Excluimos las filas DERIVADAS que el Resumen también excluye, para que estos
  // KPI de ingreso/egreso AF CUADREN con el Resumen (un solo número canónico).
  // Siguen visibles en la tabla; solo no se suman acá:
  //   - reembolso de inventario (rol='paga' + inventoryTxId): su costo ya está
  //     en Productos/totalCompras → sumarlo acá sería doble conteo.
  //   - espejo de "otros cargos" del arriendo (reservaId != null): esa plata ya
  //     está contada en Arriendos (ingreso_af_clp).
  // La recuperación (rol='recibe' + inventoryTxId) SÍ cuenta como ingreso AF,
  // igual que en useResumenLogic. (Audit completitud 2026-06-21.)
  const canonicas = rows.filter(
    (r) =>
      !(r.inventoryTxId != null && r.rolDeAlojaFacil === "paga") &&
      r.reservaId == null && r.lavanderiaId == null,
  );

  let countIngresos = 0;
  let countEgresos = 0;
  let countIntermediarios = 0; // cuenta filas, no pares
  let countOtros = 0;
  let totalIngresos = 0;
  let totalEgresos = 0;
  let totalIntermediado = 0;

  for (const r of canonicas) {
    const monto = Number(r.monto) || 0;
    const kind = clasificarFlujo(r);
    if (kind === "ingreso") {
      countIngresos += 1;
      totalIngresos += monto;
    } else if (kind === "egreso") {
      countEgresos += 1;
      totalEgresos += monto;
    } else if (kind === "intermediario") {
      countIntermediarios += 1;
      // Para NO doble-contar dinero intermediado: solo sumamos el lado
      // 'recibe' del par (representante). Las filas legacy con rol
      // 'intermediario' (sin paired_id) las sumamos directo.
      const esRepresentante =
        r.rolDeAlojaFacil === "intermediario" ||
        r.rolDeAlojaFacil === "recibe";
      if (esRepresentante) totalIntermediado += monto;
    } else {
      countOtros += 1;
    }
  }

  return {
    countTotal: canonicas.length,
    countIngresos,
    countEgresos,
    countIntermediarios,
    countOtros,
    totalIngresos,
    totalEgresos,
    totalIntermediado,
    neto: totalIngresos - totalEgresos,
  };
}

function ReadOnlyTipoCard({
  titulo,
  rows,
  columns,
  loading,
  seccion,
}: ReadOnlyCardProps) {
  const [expanded, setExpanded] = useState(false);
  const esFlujos = seccion === "Gastos";
  const esSueldos = seccion === "Sueldos";

  // Layout legacy (fallback si en el futuro entra una sección nueva):
  // cantidad + Σ monto + desglose por rolAF.
  const statsLegacy = useMemo(() => {
    const count = rows.length;
    const totalMonto = rows.reduce((s, r) => s + (Number(r.monto) || 0), 0);
    const byRol: Record<string, { count: number; monto: number }> = {};
    for (const r of rows) {
      const rol = r.rolDeAlojaFacil ?? "sin_rol";
      if (!byRol[rol]) byRol[rol] = { count: 0, monto: 0 };
      byRol[rol].count += 1;
      byRol[rol].monto += Number(r.monto) || 0;
    }
    return { count, totalMonto, byRol };
  }, [rows]);

  // Layout Flujos (round 2026-05-13): Ingreso AF / Egreso AF / Neto
  // destacado, count + intermediario como KPIs secundarios.
  const statsFlujos = useMemo(() => calcStatsFlujos(rows), [rows]);

  // Layout Sueldos (round 2026-05-13): Σ Sueldos pagados destacado +
  // promedio + desglose por destinatario.
  const statsSueldos = useMemo(() => calcStatsSueldos(rows), [rows]);

  const rolLabels: Record<string, { label: string; color: string }> = {
    paga: { label: "AF paga", color: "blue" },
    recibe: { label: "AF recibe", color: "green" },
    intermediario: { label: "Intermediario", color: "purple" },
    sin_rol: { label: "Sin rol AF", color: "default" },
  };

  const cardCount = esFlujos
    ? statsFlujos.countTotal
    : esSueldos
      ? statsSueldos.count
      : statsLegacy.count;

  // Título visual del Tag y emoji según la sección.
  const cardTagColor = esFlujos ? "geekblue" : esSueldos ? "red" : "blue";
  const cardEmoji = esFlujos ? "🔁" : esSueldos ? "💼" : "💸";
  const cardTitle = esFlujos
    ? `Flujos ${titulo.toLowerCase()}`
    : esSueldos
      ? `Sueldos ${titulo.toLowerCase()}`
      : titulo;

  return (
    <Card
      style={{ marginBottom: 16 }}
      bodyStyle={{ padding: "20px 24px" }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tag
            color={cardTagColor}
            style={{ fontSize: 13, marginRight: 0 }}
          >
            {cardEmoji} {cardTitle}
          </Tag>
          <span style={{ fontSize: 12, color: "#6b7280" }}>
            {cardCount} {cardCount === 1 ? "registro" : "registros"} en el período
          </span>
        </div>
      }
    >
      {esSueldos ? (
        // ── Layout Sueldos: recuadro principal Σ Pagado (siempre pérdida) ──
        //
        // Sueldos por definición son egreso AF (rol="paga" fijo, ver
        // normalizarSueldo en useFinanzasEmpresaLogic). El recuadro
        // principal destaca el total pagado como pérdida total del
        // período, sin ambigüedad de "neto" porque nunca hay ingreso
        // contraparte. Debajo: cantidad + promedio + desglose por
        // destinatario para que el admin pueda evaluar dónde se está
        // yendo el dinero de sueldos.
        <>
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 10,
              padding: "16px 20px",
              marginBottom: 16,
            }}
          >
            <Row gutter={[24, 12]} align="middle">
              <Col xs={24} sm={14}>
                <Statistic
                  title={
                    <span
                      style={{
                        fontSize: 13,
                        color: "#cf1322",
                        fontWeight: 700,
                      }}
                    >
                      Σ Sueldos pagados — Pérdida AF
                    </span>
                  }
                  value={statsSueldos.total}
                  precision={0}
                  formatter={(v) => fmtCLP(Number(v))}
                  valueStyle={{
                    color: "#cf1322",
                    fontSize: 28,
                    fontWeight: 700,
                  }}
                />
                <div
                  style={{
                    fontSize: 11,
                    color: "#7f1d1d",
                    marginTop: 4,
                    fontStyle: "italic",
                  }}
                >
                  Los sueldos son siempre egreso AF — el total resta
                  directamente del neto de la empresa en el período.
                </div>
              </Col>
              <Col xs={12} sm={5}>
                <Statistic
                  title={
                    <span
                      style={{
                        fontSize: 13,
                        color: "#1677ff",
                        fontWeight: 600,
                      }}
                    >
                      Cantidad
                    </span>
                  }
                  value={statsSueldos.count}
                  valueStyle={{ color: "#1677ff", fontSize: 22 }}
                />
              </Col>
              <Col xs={12} sm={5}>
                <Statistic
                  title={
                    <span
                      style={{
                        fontSize: 13,
                        color: "#7c3aed",
                        fontWeight: 600,
                      }}
                    >
                      Promedio
                    </span>
                  }
                  value={statsSueldos.promedio}
                  precision={0}
                  formatter={(v) => fmtCLP(Number(v))}
                  valueStyle={{ color: "#7c3aed", fontSize: 22 }}
                />
              </Col>
            </Row>
          </div>

          {/* Desglose por destinatario — KPIs secundarios */}
          <div>
            <div
              style={{
                fontSize: 13,
                color: "#374151",
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              Desglose por destinatario
            </div>
            {Object.entries(statsSueldos.byDestinatario).length === 0 ? (
              <span style={{ fontSize: 13, color: "#888" }}>
                Sin registros en el período.
              </span>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                {Object.entries(statsSueldos.byDestinatario)
                  .sort((a, b) => b[1].monto - a[1].monto)
                  .map(([rol, info]) => {
                    const meta =
                      DESTINATARIO_LABELS[rol] ?? {
                        label: rol,
                        color: "default",
                      };
                    return (
                      <Tag
                        key={rol}
                        color={meta.color}
                        style={{
                          fontSize: 13,
                          padding: "4px 10px",
                          marginRight: 0,
                        }}
                      >
                        {meta.label}: <b>{info.count}</b>
                        <span style={{ opacity: 0.8, marginLeft: 6 }}>
                          · {fmtCLP(info.monto)}
                        </span>
                      </Tag>
                    );
                  })}
              </div>
            )}
          </div>
        </>
      ) : esFlujos ? (
        // ── Layout Flujos: recuadro principal Ingreso/Egreso/Neto ───────
        // El recuadro está marcado con fondo gris claro y borde para
        // diferenciarlo visualmente de los KPIs secundarios. El neto
        // pinta verde si es positivo (AF gana plata en el período) y
        // rojo si es negativo (AF pierde plata).
        <>
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: "16px 20px",
              marginBottom: 16,
            }}
          >
            <Row gutter={[24, 12]} align="middle">
              <Col xs={24} sm={8}>
                <Statistic
                  title={
                    <span
                      style={{
                        fontSize: 13,
                        color: "#15803d",
                        fontWeight: 600,
                      }}
                    >
                      Σ Ingresos AF
                    </span>
                  }
                  value={statsFlujos.totalIngresos}
                  precision={0}
                  formatter={(v) => fmtCLP(Number(v))}
                  valueStyle={{ color: "#15803d", fontSize: 24 }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title={
                    <span
                      style={{
                        fontSize: 13,
                        color: "#cf1322",
                        fontWeight: 600,
                      }}
                    >
                      Σ Egresos AF
                    </span>
                  }
                  value={statsFlujos.totalEgresos}
                  precision={0}
                  formatter={(v) => fmtCLP(Number(v))}
                  valueStyle={{ color: "#cf1322", fontSize: 24 }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title={
                    <span
                      style={{
                        fontSize: 13,
                        color: "#111827",
                        fontWeight: 700,
                      }}
                    >
                      Neto (Ingresos − Egresos)
                    </span>
                  }
                  value={statsFlujos.neto}
                  precision={0}
                  formatter={(v) => fmtCLP(Number(v))}
                  valueStyle={{
                    color: statsFlujos.neto >= 0 ? "#15803d" : "#cf1322",
                    fontSize: 26,
                    fontWeight: 700,
                  }}
                />
              </Col>
            </Row>
            <div
              style={{
                fontSize: 11,
                color: "#6b7280",
                marginTop: 8,
                fontStyle: "italic",
              }}
            >
              Solo flujos de ingreso y egreso a Aloja Fácil — los
              intermediarios no afectan el neto (es dinero que solo pasa
              por AF, ver KPI más abajo). Se excluyen también las filas
              derivadas que ya cuentan en otra parte: reembolsos de inventario
              (su costo está en Productos) y espejos de "otros cargos" del
              arriendo (ya están en Arriendos). Así estos KPI cuadran con el
              Resumen — las filas siguen visibles en la tabla.
            </div>
          </div>

          {/* KPIs secundarios: counts + monto intermediado referencial */}
          <Row gutter={[24, 16]}>
            <Col xs={12} sm={6}>
              <Statistic
                title={
                  <span style={{ fontSize: 12, color: "#1677ff", fontWeight: 600 }}>
                    Cantidad total
                  </span>
                }
                value={statsFlujos.countTotal}
                valueStyle={{ color: "#1677ff", fontSize: 18 }}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title={
                  <span style={{ fontSize: 12, color: "#15803d", fontWeight: 600 }}>
                    Ingresos
                  </span>
                }
                value={statsFlujos.countIngresos}
                valueStyle={{ color: "#15803d", fontSize: 18 }}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title={
                  <span style={{ fontSize: 12, color: "#cf1322", fontWeight: 600 }}>
                    Egresos
                  </span>
                }
                value={statsFlujos.countEgresos}
                valueStyle={{ color: "#cf1322", fontSize: 18 }}
              />
            </Col>
            <Col xs={12} sm={6}>
              <div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#7c3aed",
                    fontWeight: 600,
                    marginBottom: 2,
                  }}
                >
                  Intermediarios
                </div>
                <div style={{ fontSize: 18, color: "#7c3aed", lineHeight: 1.2 }}>
                  {statsFlujos.countIntermediarios}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                  Σ {fmtCLP(statsFlujos.totalIntermediado)}{" "}
                  <span style={{ opacity: 0.7 }}>(referencial)</span>
                </div>
              </div>
            </Col>
          </Row>
          {statsFlujos.countOtros > 0 && (
            <div
              style={{
                marginTop: 12,
                fontSize: 12,
                color: "#92400e",
                background: "#fef3c7",
                border: "1px solid #fde68a",
                padding: "6px 10px",
                borderRadius: 6,
              }}
            >
              ⚠ Hay {statsFlujos.countOtros} registro
              {statsFlujos.countOtros === 1 ? "" : "s"} sin rol AF
              clasificado — no entra
              {statsFlujos.countOtros === 1 ? "" : "n"} en el neto.
              Revisar en la tabla detallada.
            </div>
          )}
        </>
      ) : (
        // ── Layout legacy (Sueldos): cantidad + Σ monto + desglose ─────
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Statistic
              title={
                <span style={{ fontSize: 13, color: "#1677ff", fontWeight: 600 }}>
                  Cantidad
                </span>
              }
              value={statsLegacy.count}
              valueStyle={{ color: "#1677ff", fontSize: 22 }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title={
                <span style={{ fontSize: 13, color: "#cf1322", fontWeight: 600 }}>
                  Σ Monto total
                </span>
              }
              value={statsLegacy.totalMonto}
              precision={0}
              formatter={(v) => fmtCLP(Number(v))}
              valueStyle={{ color: "#cf1322", fontSize: 22 }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <div>
              <div style={{ fontSize: 13, color: "#7c3aed", fontWeight: 600, marginBottom: 4 }}>
                Desglose por Rol AF
              </div>
              {Object.entries(statsLegacy.byRol).length === 0 ? (
                <span style={{ fontSize: 13, color: "#888" }}>—</span>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    alignItems: "flex-start",
                  }}
                >
                  {Object.entries(statsLegacy.byRol)
                    .sort((a, b) => b[1].monto - a[1].monto)
                    .map(([rol, info]) => {
                      const meta =
                        rolLabels[rol] ?? { label: rol, color: "default" };
                      return (
                        <Tag
                          key={rol}
                          color={meta.color}
                          style={{ marginRight: 0, fontSize: 12 }}
                        >
                          {meta.label}: <b>{info.count}</b>
                          <span style={{ opacity: 0.75, marginLeft: 4 }}>
                            · {fmtCLP(info.monto)}
                          </span>
                        </Tag>
                      );
                    })}
                </div>
              )}
            </div>
          </Col>
        </Row>
      )}

      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Button
          type="link"
          onClick={() => setExpanded((s) => !s)}
          icon={expanded ? <DownOutlined /> : <RightOutlined />}
          disabled={cardCount === 0}
        >
          {expanded
            ? "Ocultar tabla detallada"
            : cardCount === 0
              ? "Sin registros"
              : "Ver tabla detallada"}
        </Button>
      </div>

      {expanded && cardCount > 0 && (
        <div style={{ marginTop: 12 }}>
          <Table
            rowKey="id"
            loading={loading}
            dataSource={rows}
            columns={columns}
            size="middle"
            pagination={paginacionCliente({ porDefecto: 10, etiqueta: "movimiento" })}
            scroll={{ x: 1250 }}
          />
        </div>
      )}
    </Card>
  );
}

interface FinanzasReadOnlyCardsProps {
  rows: RegistroFinanza[];
  columns: ColumnsType<RegistroFinanza>;
  loading: boolean;
  subTabs: { key: Periodicidad; label: string }[];
  seccion: Seccion;
}

function FinanzasReadOnlyCards({
  rows,
  columns,
  loading,
  subTabs,
  seccion,
}: FinanzasReadOnlyCardsProps) {
  return (
    <div>
      {subTabs.map((t) => (
        <ReadOnlyTipoCard
          key={t.key}
          titulo={t.label}
          rows={rows.filter((r) => r.tipo === t.key)}
          columns={columns}
          loading={loading}
          seccion={seccion}
        />
      ))}
    </div>
  );
}
